import { getStaffServiceIds } from "@/lib/staff-associations";
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import DashboardLayout from "@/components/DashboardLayout";
import ImageUpload from "@/components/ImageUpload";
import CalendarView from "@/components/CalendarView";
import AnalyticsView from "@/components/AnalyticsView";
import EmailTemplatesManager from "@/components/EmailTemplatesManager";
import DiscountCodesManager from "@/components/DiscountCodesManager";
import ApiKeysManager from "@/components/ApiKeysManager";
import BlockedDatesManager from "@/components/BlockedDatesManager";
import StaffAbsencesManager from "@/components/StaffAbsencesManager";
import BookingsListView from "@/components/BookingsListView";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { 
  ArrowLeft, Building2, Save, Package, Users, Calendar, BookOpen,
  Plus, Pencil, Trash2, Clock, Euro, ExternalLink, User, XCircle, FolderOpen, CalendarDays,
  Palette, Link2, Unlink, Loader2, X, Percent, ToggleLeft, ToggleRight, Eye, EyeOff,
  BarChart3, Phone, Mail, FileText, UserCircle, Globe, GripVertical, Copy, Check, UserPlus, CreditCard, Link, Bell, ClipboardList, MapPin, Tag, Key, MessageCircle
} from "lucide-react";
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, rectSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { it } from "date-fns/locale";
import { toast } from "sonner";

// Sortable wrapper for service cards (Admin)
const AdminSortableServiceCard = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {children({ listeners, isDragging })}
    </div>
  );
};

// Sortable wrapper for category cards (Admin)
const AdminSortableCategoryCard = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {children({ listeners, isDragging })}
    </div>
  );
};

const AdminBusinessManage = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [business, setBusiness] = useState(null);
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [gcalStatus, setGcalStatus] = useState({ connected: false, email: null, configured: false });
  const [connectingGcal, setConnectingGcal] = useState(false);
  const [googleCalendars, setGoogleCalendars] = useState([]);
  const [loadingGoogleCalendars, setLoadingGoogleCalendars] = useState(false);
  
  // Service filter state
  const [serviceCategoryFilter, setServiceCategoryFilter] = useState("all");
  
  // Password change
  const [passwordForm, setPasswordForm] = useState({ newPass: "", confirm: "" });
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Owner assignment/update
  const [ownerForm, setOwnerForm] = useState({ email: "", name: "", password: "" });
  const [assigningOwner, setAssigningOwner] = useState(false);
  const [editingOwner, setEditingOwner] = useState(false);
  const [ownerEditForm, setOwnerEditForm] = useState({ email: "", name: "" });
  const [savingOwnerEdit, setSavingOwnerEdit] = useState(false);
  
  // Info sub-tab
  const [infoSubTab, setInfoSubTab] = useState("general");

  // Notification settings state
  const [notificationSettings, setNotificationSettings] = useState({
    booking_confirmation_title: "Prenotazione Confermata ✅",
    booking_confirmation_body: "{service_name} - {booking_date}",
    reminder_enabled: true,
    reminder_hours: [24],
    reminder_title: "Promemoria Appuntamento 🔔",
    reminder_body: "Domani alle {booking_time}: {service_name}",
    redirect_url_after_booking: "",
    recurring_appointments_enabled: false
  });
  const [firebaseUploading, setFirebaseUploading] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);

  // Booking detail dialog state
  const [bookingDetailOpen, setBookingDetailOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [editingBooking, setEditingBooking] = useState(false);
  const [bookingEditForm, setBookingEditForm] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    status: "confirmed", notes: "", start_datetime: "", start_time: ""
  });
  
  // Form data
  const [formData, setFormData] = useState({
    name: "", description: "", email: "", phone: "", address: "",
    timezone: "Europe/Rome", total_resources: 1, is_active: true, background_color: "#4F46E5",
    booking_flow_order: "service_first",
    payment_mode: "disabled",  // disabled, full, percentage
    payment_percentage: 100,
    business_type: "appointment",  // appointment, event, daily_rental
    logo_url: ""
  });
  
  // Category dialog
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: "", description: "", image_url: "", order: 0, color: "#4F46E5"
  });

  // Service dialog
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState({
    name: "", description: "", image_url: "", category_id: "",
    duration_minutes: 30, price: 0, currency: "EUR"
  });

  // Staff dialog
  const [staffDialogOpen, setStaffDialogOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [staffForm, setStaffForm] = useState({
    name: "", email: "", phone: "", image_url: "", role: "employee", service_ids: [],
    color: "",
    // Staff login fields
    has_login: false, password: "",
    // Staff permissions
    can_edit_insert: true, can_delete: false, can_view_all_staff: false
  });

  // Calendar dialog
  const [calendarDialogOpen, setCalendarDialogOpen] = useState(false);
  const [editingCalendar, setEditingCalendar] = useState(null);
  const [calendarForm, setCalendarForm] = useState({
    name: "", description: "", service_ids: [], staff_id: "",
    slot_duration_minutes: 30, buffer_before: 0, buffer_after: 0, max_days_advance: 30,
    default_availability: [
      { day_of_week: 0, periods: [{ start_time: "09:00", end_time: "13:00" }, { start_time: "14:00", end_time: "18:00" }] },
      { day_of_week: 1, periods: [{ start_time: "09:00", end_time: "13:00" }, { start_time: "14:00", end_time: "18:00" }] },
      { day_of_week: 2, periods: [{ start_time: "09:00", end_time: "13:00" }, { start_time: "14:00", end_time: "18:00" }] },
      { day_of_week: 3, periods: [{ start_time: "09:00", end_time: "13:00" }, { start_time: "14:00", end_time: "18:00" }] },
      { day_of_week: 4, periods: [{ start_time: "09:00", end_time: "13:00" }, { start_time: "14:00", end_time: "18:00" }] },
    ]
  });

  // Python convention: 0=Mon, 1=Tue, 2=Wed, 3=Thu, 4=Fri, 5=Sat, 6=Sun
  const dayNames = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

  // DnD sensors
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
  );

  // DnD: reorder services on drag end
  const handleServiceDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const filtered = services
      .filter(s => {
        if (serviceCategoryFilter === "all") return true;
        if (serviceCategoryFilter === "none") return !s.category_id;
        return s.category_id === serviceCategoryFilter;
      })
      .sort((a, b) => (a.order || 0) - (b.order || 0));

    const oldIndex = filtered.findIndex(s => s.service_id === active.id);
    const newIndex = filtered.findIndex(s => s.service_id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(filtered, oldIndex, newIndex);
    const orderMap = new Map();
    reordered.forEach((s, i) => orderMap.set(s.service_id, i));
    setServices(prev => prev.map(s => orderMap.has(s.service_id) ? { ...s, order: orderMap.get(s.service_id) } : s));

    const reorderedIds = reordered.map(s => s.service_id);
    const otherIds = services.filter(s => !reorderedIds.includes(s.service_id)).sort((a, b) => (a.order || 0) - (b.order || 0)).map(s => s.service_id);

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/admin/businesses/${businessId}/services/reorder`, { service_ids: [...reorderedIds, ...otherIds] }, { headers, withCredentials: true });
      toast.success("Ordine servizi aggiornato");
      fetchBusinessData();
    } catch {
      toast.error("Errore nel riordino dei servizi");
      fetchBusinessData();
    }
  }, [services, serviceCategoryFilter, token, businessId]);

  // DnD: reorder categories on drag end
  const handleCategoryDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const sorted = [...categories].sort((a, b) => (a.order || 0) - (b.order || 0));
    const oldIndex = sorted.findIndex(c => c.category_id === active.id);
    const newIndex = sorted.findIndex(c => c.category_id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(sorted, oldIndex, newIndex);
    setCategories(reordered.map((c, i) => ({ ...c, order: i })));

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/admin/businesses/${businessId}/categories/reorder`, {
        category_ids: reordered.map(c => c.category_id)
      }, { headers, withCredentials: true });
      toast.success("Ordine categorie aggiornato");
      fetchBusinessData();
    } catch {
      toast.error("Errore nel riordino delle categorie");
      fetchBusinessData();
    }
  }, [categories, token, businessId]);

  useEffect(() => { 
    fetchBusinessData();
    // Check for Google Calendar callback
    if (searchParams.get("gcal") === "connected") {
      toast.success("Google Calendar collegato con successo!");
    }
  }, [businessId, searchParams]);

  const fetchBusinessData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [bizRes, catRes, srvRes, staffRes, calRes, bookRes] = await Promise.all([
        axios.get(`${API}/admin/businesses/${businessId}`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/businesses/${businessId}/categories`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/businesses/${businessId}/services`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/businesses/${businessId}/staff`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/businesses/${businessId}/calendars`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/businesses/${businessId}/bookings`, { headers, withCredentials: true })
      ]);
      
      const biz = bizRes.data;
      setBusiness(biz);
      setFormData({
        name: biz.name || "", description: biz.description || "", email: biz.email || "",
        phone: biz.phone || "", address: biz.address || "", timezone: biz.timezone || "Europe/Rome",
        total_resources: biz.total_resources || 1, is_active: biz.is_active !== false,
        background_color: biz.background_color || "#4F46E5",
        latitude: biz.latitude || "", longitude: biz.longitude || "",
        booking_flow_order: biz.booking_flow_order || "service_first",
        payment_mode: biz.payment_mode || "disabled",
        payment_percentage: biz.payment_percentage || 100,
        business_type: biz.business_type || "appointment",
        logo_url: biz.logo_url || ""
      });
      
      // Load notification settings
      const ns = biz.notification_settings || {};
      setNotificationSettings({
        booking_confirmation_title: ns.booking_confirmation_title || "Prenotazione Confermata ✅",
        booking_confirmation_body: ns.booking_confirmation_body || "{service_name} - {booking_date}",
        reminder_enabled: ns.reminder_enabled !== false,
        reminder_hours: ns.reminder_hours || [24],
        reminder_title: ns.reminder_title || "Promemoria Appuntamento 🔔",
        reminder_body: ns.reminder_body || "Domani alle {booking_time}: {service_name}",
        redirect_url_after_booking: biz.redirect_url_after_booking || "",
        recurring_appointments_enabled: biz.recurring_appointments_enabled || false
      });
      
      setCategories(catRes.data || []);
      setServices(srvRes.data || []);
      setStaff(staffRes.data || []);
      setCalendars(calRes.data || []);
      setBookings(bookRes.data || []);
      
      // Fetch Google Calendar status
      try {
        const gcalRes = await axios.get(`${API}/business/${businessId}/google-calendar/status`, { headers });
        setGcalStatus(gcalRes.data);
      } catch (e) {
        console.log("Google Calendar status not available");
      }
    } catch (error) {
      toast.error("Errore nel caricamento dell'attività");
      navigate("/admin");
    } finally {
      setLoading(false);
    }
  };

  const handleConnectGoogleCalendar = async () => {
    setConnectingGcal(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/business/${businessId}/google-calendar/auth-url`, { headers });
      window.location.href = res.data.authorization_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella connessione a Google Calendar");
      setConnectingGcal(false);
    }
  };

  const handleDisconnectGoogleCalendar = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/business/${businessId}/google-calendar/disconnect`, {}, { headers });
      setGcalStatus({ connected: false, email: null, configured: gcalStatus.configured });
      setGoogleCalendars([]);
      toast.success("Google Calendar disconnesso");
    } catch (error) {
      toast.error("Errore nella disconnessione");
    }
  };

  const fetchGoogleCalendars = async () => {
    if (!gcalStatus.connected) return;
    setLoadingGoogleCalendars(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/business/${businessId}/google-calendar/calendars`, { headers });
      setGoogleCalendars(res.data.calendars || []);
    } catch (error) {
      console.error("Error fetching Google calendars:", error);
    } finally {
      setLoadingGoogleCalendars(false);
    }
  };

  const handleSelectGoogleCalendar = async (calendarId, calendarName) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/business/${businessId}/google-calendar/select`, {
        calendar_id: calendarId,
        calendar_name: calendarName
      }, { headers });
      setGcalStatus(prev => ({ ...prev, selected_calendar_id: calendarId, selected_calendar_name: calendarName }));
      toast.success(`Calendario "${calendarName}" selezionato`);
    } catch (error) {
      toast.error("Errore nella selezione del calendario");
    }
  };

  useEffect(() => {
    if (gcalStatus.connected) {
      fetchGoogleCalendars();
    }
  }, [gcalStatus.connected]);

  const handleSaveSettings = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      await axios.put(`${API}/admin/businesses/${businessId}`, formData, {
        headers: { Authorization: `Bearer ${token}` }, withCredentials: true
      });
      toast.success("Impostazioni salvate!");
      fetchBusinessData();
    } catch (error) {
      toast.error("Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  // Save notification settings
  const handleSaveNotificationSettings = async () => {
    setSavingNotifications(true);
    try {
      await axios.put(
        `${API}/admin/business/${businessId}/settings`,
        {
          notification_settings: {
            booking_confirmation_title: notificationSettings.booking_confirmation_title,
            booking_confirmation_body: notificationSettings.booking_confirmation_body,
            reminder_enabled: notificationSettings.reminder_enabled,
            reminder_hours: notificationSettings.reminder_hours,
            reminder_title: notificationSettings.reminder_title,
            reminder_body: notificationSettings.reminder_body
          },
          redirect_url_after_booking: notificationSettings.redirect_url_after_booking || null,
          recurring_appointments_enabled: notificationSettings.recurring_appointments_enabled
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Impostazioni notifiche salvate!");
      fetchBusinessData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSavingNotifications(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.newPass !== passwordForm.confirm) {
      toast.error("Le password non coincidono");
      return;
    }
    if (passwordForm.newPass.length < 6) {
      toast.error("La password deve avere almeno 6 caratteri");
      return;
    }
    setChangingPassword(true);
    try {
      // Use the correct endpoint based on role
      // Admin/SuperAdmin changes owner password, Business Owner changes own password
      const endpoint = (user?.role === "admin" || user?.role === "super_admin")
        ? `${API}/admin/businesses/${businessId}/change-owner-password`
        : `${API}/auth/change-password`;
      
      await axios.post(endpoint, {
        new_password: passwordForm.newPass
      }, {
        headers: { Authorization: `Bearer ${token}` }, withCredentials: true
      });
      toast.success("Password cambiata con successo!");
      setPasswordForm({ newPass: "", confirm: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel cambio password");
    } finally {
      setChangingPassword(false);
    }
  };
  
  // Assign Owner
  const handleAssignOwner = async (e) => {
    e.preventDefault();
    if (!ownerForm.email) {
      toast.error("L'email è obbligatoria");
      return;
    }
    setAssigningOwner(true);
    try {
      const response = await axios.post(`${API}/admin/businesses/${businessId}/assign-owner`, {
        email: ownerForm.email,
        name: ownerForm.name,
        password: ownerForm.password
      }, {
        headers: { Authorization: `Bearer ${token}` }, withCredentials: true
      });
      toast.success(response.data.message);
      setOwnerForm({ email: "", name: "", password: "" });
      fetchBusinessData(); // Refresh to show new owner
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'assegnazione del proprietario");
    } finally {
      setAssigningOwner(false);
    }
  };

  // Update Owner Email/Name
  const handleUpdateOwner = async (e) => {
    e.preventDefault();
    if (!ownerEditForm.email) {
      toast.error("L'email è obbligatoria");
      return;
    }
    setSavingOwnerEdit(true);
    try {
      await axios.put(`${API}/admin/businesses/${businessId}/update-owner`, {
        email: ownerEditForm.email,
        name: ownerEditForm.name
      }, {
        headers: { Authorization: `Bearer ${token}` }, withCredentials: true
      });
      toast.success("Proprietario aggiornato con successo");
      setEditingOwner(false);
      fetchBusinessData(); // Refresh to show updated owner
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento del proprietario");
    } finally {
      setSavingOwnerEdit(false);
    }
  };

  // ==================== COPY LINK FUNCTIONS ====================
  const [copiedCalendarId, setCopiedCalendarId] = useState(null);
  
  const copyCalendarLink = async (calendarId) => {
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/book/${business?.slug}?calendar=${calendarId}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopiedCalendarId(calendarId);
      toast.success("Link copiato!");
      setTimeout(() => setCopiedCalendarId(null), 2000);
    } catch (error) {
      toast.error("Errore nella copia del link");
    }
  };

  // ==================== BOOKING DETAIL FUNCTIONS ====================
  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    // Remove trailing Z to treat as local time (not UTC)
    const cleanDateStr = dateStr.replace(/Z$/, '');
    const date = new Date(cleanDateStr);
    return date.toLocaleDateString("it-IT", { 
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" 
    });
  };

  const openBookingDetail = (booking) => {
    setSelectedBooking(booking);
    const startDate = booking.start_datetime ? parseISO(booking.start_datetime) : new Date();
    setBookingEditForm({
      customer_name: booking.customer_name || "",
      customer_email: booking.customer_email || "",
      customer_phone: booking.customer_phone || "",
      status: booking.status || "confirmed",
      notes: booking.notes || "",
      start_datetime: format(startDate, "yyyy-MM-dd"),
      start_time: format(startDate, "HH:mm")
    });
    setEditingBooking(false);
    setBookingDetailOpen(true);
  };

  const handleUpdateBooking = async () => {
    if (!selectedBooking) return;
    try {
      const [year, month, day] = bookingEditForm.start_datetime.split('-').map(Number);
      const [hours, minutes] = bookingEditForm.start_time.split(':').map(Number);
      const startDateTime = new Date(year, month - 1, day, hours, minutes);
      
      await axios.put(`${API}/admin/businesses/${businessId}/bookings/${selectedBooking.booking_id}`, {
        customer_name: bookingEditForm.customer_name,
        customer_email: bookingEditForm.customer_email,
        customer_phone: bookingEditForm.customer_phone,
        status: bookingEditForm.status,
        notes: bookingEditForm.notes,
        start_datetime: startDateTime.toISOString()
      }, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      
      toast.success("Prenotazione aggiornata!");
      setBookingDetailOpen(false);
      fetchBusinessData();
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const handleDeleteBooking = async () => {
    if (!selectedBooking || !confirm("Eliminare questa prenotazione?")) return;
    try {
      await axios.delete(`${API}/admin/businesses/${businessId}/bookings/${selectedBooking.booking_id}`, {
        headers: { Authorization: `Bearer ${token}` }, withCredentials: true
      });
      toast.success("Prenotazione eliminata!");
      setBookingDetailOpen(false);
      fetchBusinessData();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  // ==================== CATEGORIES ====================
  const handleSaveCategory = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (editingCategory) {
        await axios.put(`${API}/admin/businesses/${businessId}/categories/${editingCategory.category_id}`, categoryForm, { headers, withCredentials: true });
        toast.success("Categoria aggiornata!");
      } else {
        await axios.post(`${API}/admin/businesses/${businessId}/categories`, categoryForm, { headers, withCredentials: true });
        toast.success("Categoria creata!");
      }
      setCategoryDialogOpen(false);
      setEditingCategory(null);
      setCategoryForm({ name: "", description: "", image_url: "", order: 0, color: "#4F46E5" });
      fetchBusinessData();
    } catch (error) {
      toast.error("Errore durante il salvataggio");
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!confirm("Eliminare questa categoria? I servizi non verranno eliminati.")) return;
    try {
      await axios.delete(`${API}/admin/businesses/${businessId}/categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` }, withCredentials: true
      });
      toast.success("Categoria eliminata!");
      fetchBusinessData();
    } catch (error) {
      toast.error("Errore durante l'eliminazione");
    }
  };

  // ==================== SERVICES ====================
  const handleSaveService = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (editingService) {
        await axios.put(`${API}/admin/businesses/${businessId}/services/${editingService.service_id}`, serviceForm, { headers, withCredentials: true });
        toast.success("Servizio aggiornato!");
      } else {
        await axios.post(`${API}/admin/businesses/${businessId}/services`, serviceForm, { headers, withCredentials: true });
        toast.success("Servizio creato!");
      }
      setServiceDialogOpen(false);
      setEditingService(null);
      setServiceForm({ name: "", description: "", image_url: "", category_id: "", duration_minutes: 30, price: 0, currency: "EUR" });
      fetchBusinessData();
    } catch (error) {
      toast.error("Errore durante il salvataggio");
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!confirm("Eliminare questo servizio?")) return;
    try {
      await axios.delete(`${API}/admin/businesses/${businessId}/services/${serviceId}`, {
        headers: { Authorization: `Bearer ${token}` }, withCredentials: true
      });
      toast.success("Servizio eliminato!");
      fetchBusinessData();
    } catch (error) {
      toast.error("Errore durante l'eliminazione");
    }
  };

  const handleToggleServiceActive = async (service) => {
    try {
      const newStatus = service.is_active === false ? true : false;
      await axios.put(`${API}/admin/businesses/${businessId}/services/${service.service_id}`, {
        ...service,
        is_active: newStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }, withCredentials: true
      });
      toast.success(newStatus ? "Servizio attivato!" : "Servizio disattivato!");
      fetchBusinessData();
    } catch (error) {
      toast.error("Errore durante l'aggiornamento");
    }
  };

  // ==================== STAFF ====================
  const handleSaveStaff = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (editingStaff) {
        await axios.put(`${API}/admin/businesses/${businessId}/staff/${editingStaff.staff_id}`, staffForm, { headers, withCredentials: true });
        toast.success("Staff aggiornato!");
      } else {
        await axios.post(`${API}/admin/businesses/${businessId}/staff`, staffForm, { headers, withCredentials: true });
        toast.success("Staff creato!");
      }
      setStaffDialogOpen(false);
      setEditingStaff(null);
      setStaffForm({ name: "", email: "", phone: "", image_url: "", role: "employee", service_ids: [], is_active: true, color: "", has_login: false, password: "", can_edit_insert: true, can_delete: false, can_view_all_staff: false });
      fetchBusinessData();
    } catch (error) {
      toast.error("Errore durante il salvataggio");
    }
  };

  const handleDeleteStaff = async (staffId) => {
    if (!confirm("Eliminare questo membro dello staff?")) return;
    try {
      await axios.delete(`${API}/admin/businesses/${businessId}/staff/${staffId}`, {
        headers: { Authorization: `Bearer ${token}` }, withCredentials: true
      });
      toast.success("Staff eliminato!");
      fetchBusinessData();
    } catch (error) {
      toast.error("Errore durante l'eliminazione");
    }
  };

  const handleToggleStaffActive = async (member) => {
    try {
      const newStatus = member.is_active === false ? true : false;
      await axios.put(`${API}/admin/businesses/${businessId}/staff/${member.staff_id}`, {
        ...member,
        is_active: newStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }, withCredentials: true
      });
      toast.success(newStatus ? "Staff attivato!" : "Staff disattivato!");
      fetchBusinessData();
    } catch (error) {
      toast.error("Errore durante l'aggiornamento");
    }
  };

  // ==================== CALENDARS ====================
  const handleSaveCalendar = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (editingCalendar) {
        await axios.put(`${API}/admin/businesses/${businessId}/calendars/${editingCalendar.calendar_id}`, calendarForm, { headers, withCredentials: true });
        toast.success("Calendario aggiornato!");
      } else {
        await axios.post(`${API}/admin/businesses/${businessId}/calendars`, calendarForm, { headers, withCredentials: true });
        toast.success("Calendario creato!");
      }
      setCalendarDialogOpen(false);
      setEditingCalendar(null);
      fetchBusinessData();
    } catch (error) {
      toast.error("Errore durante il salvataggio");
    }
  };

  const handleDeleteCalendar = async (calendarId) => {
    if (!confirm("Eliminare questo calendario?")) return;
    try {
      await axios.delete(`${API}/admin/businesses/${businessId}/calendars/${calendarId}`, {
        headers: { Authorization: `Bearer ${token}` }, withCredentials: true
      });
      toast.success("Calendario eliminato!");
      fetchBusinessData();
    } catch (error) {
      toast.error("Errore durante l'eliminazione");
    }
  };

  const openEditCalendar = (cal) => {
    setEditingCalendar(cal);
    setCalendarForm({
      name: cal.name || "", description: cal.description || "", service_ids: cal.service_ids || [],
      staff_id: cal.staff_id || "", slot_duration_minutes: cal.slot_duration_minutes || 30,
      buffer_before: cal.buffer_before || 0, buffer_after: cal.buffer_after || 0,
      max_days_advance: cal.max_days_advance || 30, default_availability: cal.default_availability || []
    });
    setCalendarDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    const styles = { pending: "bg-amber-100 text-amber-800", confirmed: "bg-blue-100 text-blue-800", completed: "bg-emerald-100 text-emerald-800", cancelled: "bg-red-100 text-red-800" };
    const labels = { pending: "In Attesa", confirmed: "Confermata", completed: "Completata", cancelled: "Cancellata" };
    return <Badge className={styles[status] || "bg-slate-100"}>{labels[status] || status}</Badge>;
  };

  if (loading) {
    return <DashboardLayout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-business-manage">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/admin")}><ArrowLeft className="h-5 w-5" /></Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{business?.name}</h1>
              <Badge className={formData.is_active ? "bg-emerald-100 text-emerald-800" : "bg-slate-100"}>{formData.is_active ? "Attiva" : "Inattiva"}</Badge>
            </div>
            <p className="text-slate-500">/book/{business?.slug}</p>
          </div>
          <a href={`/book/${business?.slug}`} target="_blank" rel="noopener noreferrer">
            <Button variant="outline"><ExternalLink className="h-4 w-4 mr-2" />Pagina Prenotazione</Button>
          </a>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
          <Card className="border-slate-100"><CardContent className="p-4"><div className="flex items-center gap-3"><FolderOpen className="h-8 w-8 text-violet-500" /><div><p className="text-2xl font-bold">{categories.length}</p><p className="text-sm text-slate-500">Categorie</p></div></div></CardContent></Card>
          <Card className="border-slate-100"><CardContent className="p-4"><div className="flex items-center gap-3"><Package className="h-8 w-8 text-blue-500" /><div><p className="text-2xl font-bold">{services.length}</p><p className="text-sm text-slate-500">Servizi</p></div></div></CardContent></Card>
          <Card className="border-slate-100"><CardContent className="p-4"><div className="flex items-center gap-3"><Users className="h-8 w-8 text-indigo-500" /><div><p className="text-2xl font-bold">{staff.length}</p><p className="text-sm text-slate-500">Staff</p></div></div></CardContent></Card>
          <Card className="border-slate-100"><CardContent className="p-4"><div className="flex items-center gap-3"><Calendar className="h-8 w-8 text-purple-500" /><div><p className="text-2xl font-bold">{calendars.length}</p><p className="text-sm text-slate-500">Calendari</p></div></div></CardContent></Card>
          <Card className="border-slate-100"><CardContent className="p-4"><div className="flex items-center gap-3"><BookOpen className="h-8 w-8 text-amber-500" /><div><p className="text-2xl font-bold">{bookings.length}</p><p className="text-sm text-slate-500">Prenotazioni</p></div></div></CardContent></Card>
          <Card className="border-slate-100"><CardContent className="p-4"><div className="flex items-center gap-3"><Euro className="h-8 w-8 text-emerald-500" /><div><p className="text-2xl font-bold">€{bookings.filter(b => b.payment_status === 'paid').reduce((sum, b) => sum + (b.payment_amount || 0), 0).toFixed(0)}</p><p className="text-sm text-slate-500">Ricavi</p></div></div></CardContent></Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="calendar-view" className="w-full">
          <TabsList className="flex flex-wrap gap-1 h-auto p-1 max-w-5xl">
            <TabsTrigger value="calendar-view"><CalendarDays className="h-4 w-4 mr-1" />Agenda</TabsTrigger>
            <TabsTrigger value="bookings"><ClipboardList className="h-4 w-4 mr-1" />Presenze</TabsTrigger>
            <TabsTrigger value="settings"><Building2 className="h-4 w-4 mr-1" />Info</TabsTrigger>
            <TabsTrigger value="categories"><FolderOpen className="h-4 w-4 mr-1" />Categorie</TabsTrigger>
            <TabsTrigger value="services"><Package className="h-4 w-4 mr-1" />Servizi</TabsTrigger>
            <TabsTrigger value="staff"><Users className="h-4 w-4 mr-1" />Staff</TabsTrigger>
            <TabsTrigger value="calendars"><Calendar className="h-4 w-4 mr-1" />Orari</TabsTrigger>
            <TabsTrigger value="analytics"><BarChart3 className="h-4 w-4 mr-1" />Analytics</TabsTrigger>
            <TabsTrigger value="discounts" data-testid="tab-discounts"><Tag className="h-4 w-4 mr-1" />Sconti</TabsTrigger>
            <TabsTrigger value="api-keys" data-testid="tab-api-keys"><Key className="h-4 w-4 mr-1" />API</TabsTrigger>
          </TabsList>

          {/* Calendar View Tab */}
          <TabsContent value="calendar-view" className="mt-6">
            <CalendarView 
              businessId={businessId} 
              staff={staff} 
              bookings={bookings} 
              calendars={calendars}
              categories={categories}
              services={services}
              isAdmin={true}
              onBookingCreated={fetchBusinessData}
            />
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="mt-6">
            {/* Sub-tabs for Settings */}
            <div className="flex flex-wrap gap-2 mb-6 border-b pb-4">
              <Button 
                variant={infoSubTab === "general" ? "default" : "outline"} 
                size="sm"
                onClick={() => setInfoSubTab("general")}
              >
                <Building2 className="h-4 w-4 mr-1" />Dati Attività
              </Button>
              <Button 
                variant={infoSubTab === "owner" ? "default" : "outline"} 
                size="sm"
                onClick={() => setInfoSubTab("owner")}
              >
                <User className="h-4 w-4 mr-1" />Proprietario
              </Button>
              <Button 
                variant={infoSubTab === "notifications" ? "default" : "outline"} 
                size="sm"
                onClick={() => setInfoSubTab("notifications")}
              >
                <Bell className="h-4 w-4 mr-1" />Notifiche
              </Button>
              <Button 
                variant={infoSubTab === "integration" ? "default" : "outline"} 
                size="sm"
                onClick={() => setInfoSubTab("integration")}
              >
                <Globe className="h-4 w-4 mr-1" />Integrazioni
              </Button>
              <Button 
                variant={infoSubTab === "payment" ? "default" : "outline"} 
                size="sm"
                onClick={() => setInfoSubTab("payment")}
              >
                <CreditCard className="h-4 w-4 mr-1" />Pagamenti
              </Button>
              <Button 
                variant={infoSubTab === "email" ? "default" : "outline"} 
                size="sm"
                onClick={() => setInfoSubTab("email")}
              >
                <Mail className="h-4 w-4 mr-1" />Email
              </Button>
              <Button 
                variant={infoSubTab === "whatsapp" ? "default" : "outline"} 
                size="sm"
                onClick={() => setInfoSubTab("whatsapp")}
              >
                <MessageCircle className="h-4 w-4 mr-1" />WhatsApp
              </Button>
            </div>
            
            {/* GENERAL - Dati Attività */}
            {infoSubTab === "general" && (
              <Card className="border-slate-100">
                <CardHeader><CardTitle>Dati Attività</CardTitle><CardDescription>Informazioni principali dell&apos;attività</CardDescription></CardHeader>
                <CardContent>
                  <form onSubmit={handleSaveSettings} className="space-y-4 max-w-xl">
                    {/* Logo Upload */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2"><Building2 className="h-4 w-4" />Logo Azienda</Label>
                      <ImageUpload 
                        value={formData.logo_url} 
                        onChange={(url) => setFormData({ ...formData, logo_url: url })} 
                      />
                    </div>
                    
                    <div className="space-y-2"><Label>Nome Attività</Label><Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required /></div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2"><Label>Email</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required /></div>
                      <div className="space-y-2"><Label>Telefono</Label><Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} /></div>
                    </div>
                    <div className="space-y-2"><Label>Indirizzo</Label><Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} /></div>
                    
                    {/* Coordinate Mappa */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2"><MapPin className="h-4 w-4" />Coordinate Mappa (per email)</Label>
                      <p className="text-xs text-slate-500">Inserisci le coordinate per mostrare la mappa nelle email di conferma</p>
                      <div className="flex gap-3">
                        <div className="flex-1">
                          <Label className="text-xs text-slate-500">Latitudine</Label>
                          <Input type="number" step="any" value={formData.latitude || ""} onChange={(e) => setFormData({...formData, latitude: e.target.value})} placeholder="45.4642" data-testid="admin-latitude-input" />
                        </div>
                        <div className="flex-1">
                          <Label className="text-xs text-slate-500">Longitudine</Label>
                          <Input type="number" step="any" value={formData.longitude || ""} onChange={(e) => setFormData({...formData, longitude: e.target.value})} placeholder="10.6313" data-testid="admin-longitude-input" />
                        </div>
                      </div>
                      {formData.latitude && formData.longitude && (
                        <a href={`https://www.google.com/maps/search/?api=1&query=${formData.latitude},${formData.longitude}`} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1 mt-1">
                          <MapPin className="h-3 w-3" /> Verifica su Google Maps
                        </a>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2"><Label>Posti/Risorse</Label><Input type="number" min="1" value={formData.total_resources} onChange={(e) => setFormData({ ...formData, total_resources: parseInt(e.target.value) || 1 })} /></div>
                      <div className="space-y-2"><Label>Stato</Label>
                        <Select value={formData.is_active ? "active" : "inactive"} onValueChange={(v) => setFormData({ ...formData, is_active: v === "active" })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="active">Attiva</SelectItem><SelectItem value="inactive">Inattiva</SelectItem></SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2"><Label>Descrizione</Label><Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} /></div>
                    
                    {/* Color Theme */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2"><Palette className="h-4 w-4" />Colore Tema</Label>
                      <div className="flex items-center gap-3">
                        <input type="color" value={formData.background_color || "#4F46E5"} onChange={(e) => setFormData({ ...formData, background_color: e.target.value })} className="w-12 h-10 rounded border cursor-pointer" />
                        <Input value={formData.background_color || "#4F46E5"} onChange={(e) => setFormData({ ...formData, background_color: e.target.value })} className="w-32" placeholder="#4F46E5" />
                        <div className="h-10 flex-1 rounded-lg flex items-center justify-center text-white text-sm font-medium" style={{ backgroundColor: formData.background_color || "#4F46E5" }}>Anteprima</div>
                      </div>
                    </div>

                    {/* Ordine Prenotazione */}
                    <div className="space-y-2 pt-4 border-t">
                      <Label className="flex items-center gap-2"><Users className="h-4 w-4" />Ordine Prenotazione</Label>
                      <p className="text-xs text-slate-500 mb-2">Scegli se mostrare prima lo staff o il servizio nella pagina di prenotazione</p>
                      <div className="flex gap-2">
                        <Button type="button" variant={formData.booking_flow_order === "service_first" ? "default" : "outline"} size="sm" onClick={() => setFormData({ ...formData, booking_flow_order: "service_first" })}>
                          <Package className="h-4 w-4 mr-1" />Prima il Servizio
                        </Button>
                        <Button type="button" variant={formData.booking_flow_order === "staff_first" ? "default" : "outline"} size="sm" onClick={() => setFormData({ ...formData, booking_flow_order: "staff_first" })}>
                          <User className="h-4 w-4 mr-1" />Prima lo Staff
                        </Button>
                      </div>
                    </div>
                    
                    <Button type="submit" disabled={saving}><Save className="h-4 w-4 mr-2" />{saving ? "Salvataggio..." : "Salva"}</Button>
                  </form>
                </CardContent>
              </Card>
            )}
            
            {/* OWNER - Proprietario */}
            {infoSubTab === "owner" && (
              <div className="space-y-6">
                {/* Current Owner Info */}
                <Card className="border-slate-100">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2"><User className="h-5 w-5" />Proprietario Attività</CardTitle>
                        <CardDescription>
                          {business?.owner_email 
                            ? <>Account proprietario: <span className="font-medium text-primary">{business.owner_email}</span></>
                            : <span className="text-amber-600">Nessun proprietario assegnato</span>
                          }
                        </CardDescription>
                      </div>
                      {(user?.role === "admin" || user?.role === "super_admin") && business?.owner_email && !editingOwner && (
                        <Button variant="outline" size="sm" onClick={() => {
                          setOwnerEditForm({ email: business.owner_email, name: business.owner_name || "" });
                          setEditingOwner(true);
                        }}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Modifica
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  {/* Edit Owner Form */}
                  {editingOwner && (user?.role === "admin" || user?.role === "super_admin") && (
                    <CardContent className="border-t pt-4">
                      <form onSubmit={handleUpdateOwner} className="space-y-4 max-w-md">
                        <div className="space-y-2">
                          <Label>Email Proprietario *</Label>
                          <Input 
                            type="email" 
                            value={ownerEditForm.email} 
                            onChange={(e) => setOwnerEditForm({...ownerEditForm, email: e.target.value})}
                            placeholder="nuova.email@esempio.com"
                            required
                          />
                          <p className="text-xs text-slate-500">Modifica l&apos;email dell&apos;account proprietario</p>
                        </div>
                        <div className="space-y-2">
                          <Label>Nome</Label>
                          <Input 
                            value={ownerEditForm.name} 
                            onChange={(e) => setOwnerEditForm({...ownerEditForm, name: e.target.value})}
                            placeholder="Mario Rossi"
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button type="submit" disabled={savingOwnerEdit}>
                            {savingOwnerEdit ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                            Salva
                          </Button>
                          <Button type="button" variant="outline" onClick={() => setEditingOwner(false)}>
                            Annulla
                          </Button>
                        </div>
                      </form>
                    </CardContent>
                  )}
                </Card>

                {/* Assign Owner - Only for SuperAdmin/Admin */}
                {(user?.role === "admin" || user?.role === "super_admin") && !business?.owner_email && (
                  <Card className="border-amber-200 bg-amber-50">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-amber-800">
                        <UserPlus className="h-5 w-5" />Assegna Proprietario
                      </CardTitle>
                      <CardDescription className="text-amber-700">
                        Inserisci l&apos;email di un utente esistente o crea un nuovo account
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <form onSubmit={handleAssignOwner} className="space-y-4 max-w-md">
                        <div className="space-y-2">
                          <Label>Email *</Label>
                          <Input 
                            type="email" 
                            value={ownerForm.email} 
                            onChange={(e) => setOwnerForm({...ownerForm, email: e.target.value})}
                            placeholder="email@esempio.com"
                            required
                          />
                          <p className="text-xs text-slate-500">Se l&apos;utente esiste, verrà assegnato. Altrimenti verrà creato.</p>
                        </div>
                        <div className="space-y-2">
                          <Label>Nome (per nuovo utente)</Label>
                          <Input 
                            value={ownerForm.name} 
                            onChange={(e) => setOwnerForm({...ownerForm, name: e.target.value})}
                            placeholder="Mario Rossi"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Password (per nuovo utente)</Label>
                          <Input 
                            type="password" 
                            value={ownerForm.password} 
                            onChange={(e) => setOwnerForm({...ownerForm, password: e.target.value})}
                            placeholder="••••••••"
                          />
                        </div>
                        <Button type="submit" disabled={assigningOwner}>
                          {assigningOwner ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}
                          Assegna Proprietario
                        </Button>
                      </form>
                    </CardContent>
                  </Card>
                )}

                {/* Change Password - Only if owner exists */}
                {business?.owner_email && (
                  <Card className="border-slate-100">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2"><Eye className="h-5 w-5" />Cambia Password</CardTitle>
                      <CardDescription>
                        {(user?.role === "admin" || user?.role === "super_admin") 
                          ? "Modifica la password del proprietario"
                          : "Modifica la tua password"}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
                        <div className="space-y-2">
                          <Label>Nuova Password</Label>
                          <Input 
                            type="password" 
                            value={passwordForm.newPass} 
                            onChange={(e) => setPasswordForm({...passwordForm, newPass: e.target.value})}
                            placeholder="••••••••"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Conferma Password</Label>
                          <Input 
                            type="password" 
                            value={passwordForm.confirm} 
                            onChange={(e) => setPasswordForm({...passwordForm, confirm: e.target.value})}
                            placeholder="••••••••"
                            required
                          />
                        </div>
                        <Button type="submit" disabled={changingPassword}>
                          {changingPassword ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                          Cambia Password
                        </Button>
                      </form>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
            
            {/* NOTIFICATIONS - Notifiche Push */}
            {infoSubTab === "notifications" && (
              <div className="space-y-6">
                {/* Firebase Config */}
                <Card className="border-slate-100">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2"><Bell className="h-5 w-5" />Firebase Push Notifications</CardTitle>
                    <CardDescription>Configura le notifiche push per questa azienda</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label>Firebase Service Account (JSON)</Label>
                      <p className="text-sm text-slate-500 mb-2">
                        Firebase Console → Impostazioni → Account di servizio → Genera nuova chiave privata
                      </p>
                      <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-primary transition-colors">
                        <input
                          type="file"
                          id="firebase-json-upload"
                          accept=".json"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            
                            const reader = new FileReader();
                            reader.onload = async (event) => {
                              try {
                                const jsonContent = event.target?.result;
                                const parsed = JSON.parse(jsonContent);
                                if (!parsed.project_id || !parsed.private_key) {
                                  toast.error("File JSON non valido");
                                  return;
                                }
                                
                                setFirebaseUploading(true);
                                try {
                                  const res = await axios.put(
                                    `${API}/admin/business/${businessId}/firebase-config`,
                                    { service_account_json: jsonContent },
                                    { headers: { Authorization: `Bearer ${token}` } }
                                  );
                                  toast.success(`Firebase configurato! Progetto: ${res.data.project_id}`);
                                  fetchBusinessData();
                                } catch (error) {
                                  toast.error(error.response?.data?.detail || "Errore nel salvataggio");
                                } finally {
                                  setFirebaseUploading(false);
                                }
                              } catch (err) {
                                toast.error("File JSON non valido");
                              }
                            };
                            reader.readAsText(file);
                          }}
                        />
                        <label htmlFor="firebase-json-upload" className="cursor-pointer">
                          <div className="flex flex-col items-center gap-2">
                            {firebaseUploading ? (
                              <Loader2 className="h-8 w-8 text-primary animate-spin" />
                            ) : (
                              <FileText className="h-8 w-8 text-slate-400" />
                            )}
                            <span className="text-sm text-slate-600">
                              Clicca per caricare <strong>serviceAccountKey.json</strong>
                            </span>
                          </div>
                        </label>
                      </div>
                    </div>
                    
                    {business?.firebase_project_id && (
                      <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <Check className="h-5 w-5 text-green-600" />
                        <span className="text-sm text-green-800">
                          Firebase configurato - Progetto: {business.firebase_project_id}
                        </span>
                      </div>
                    )}
                    
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-medium text-blue-900 mb-2">Importante per iOS</h4>
                      <p className="text-sm text-blue-800">
                        Carica la <strong>APNs Auth Key</strong> in Firebase Console → Cloud Messaging → Configurazione app Apple.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Notification Text Settings */}
                <Card className="border-slate-100">
                  <CardHeader>
                    <CardTitle>Testo Notifiche</CardTitle>
                    <CardDescription>Personalizza il testo delle notifiche push inviate ai clienti</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4 max-w-xl">
                    <div className="space-y-2">
                      <Label>Titolo conferma prenotazione</Label>
                      <Input
                        placeholder="Prenotazione Confermata ✅"
                        value={notificationSettings.booking_confirmation_title}
                        onChange={(e) => setNotificationSettings({...notificationSettings, booking_confirmation_title: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Testo conferma (usa {"{service_name}"}, {"{booking_date}"})</Label>
                      <Input
                        placeholder="{service_name} - {booking_date}"
                        value={notificationSettings.booking_confirmation_body}
                        onChange={(e) => setNotificationSettings({...notificationSettings, booking_confirmation_body: e.target.value})}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Reminder Settings */}
                <Card className="border-slate-100">
                  <CardHeader>
                    <CardTitle>Promemoria</CardTitle>
                    <CardDescription>Configura i promemoria automatici prima degli appuntamenti</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4 max-w-xl">
                    <div className="flex items-center gap-3">
                      <Switch 
                        id="reminder-enabled" 
                        checked={notificationSettings.reminder_enabled}
                        onCheckedChange={(checked) => setNotificationSettings({...notificationSettings, reminder_enabled: checked})}
                      />
                      <Label htmlFor="reminder-enabled">Abilita promemoria</Label>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Invia promemoria (ore prima)</Label>
                      <div className="flex flex-wrap gap-3 mt-2">
                        {[24, 12, 6, 2, 1].map(h => (
                          <label key={h} className="flex items-center gap-1 text-sm cursor-pointer">
                            <input 
                              type="checkbox" 
                              checked={notificationSettings.reminder_hours.includes(h)}
                              onChange={(e) => {
                                const hours = e.target.checked 
                                  ? [...notificationSettings.reminder_hours, h]
                                  : notificationSettings.reminder_hours.filter(x => x !== h);
                                setNotificationSettings({...notificationSettings, reminder_hours: hours});
                              }}
                              className="rounded" 
                            />
                            {h}h
                          </label>
                        ))}
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Titolo promemoria</Label>
                      <Input
                        placeholder="Promemoria Appuntamento 🔔"
                        value={notificationSettings.reminder_title}
                        onChange={(e) => setNotificationSettings({...notificationSettings, reminder_title: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Testo promemoria</Label>
                      <Input
                        placeholder="Domani alle {booking_time}: {service_name}"
                        value={notificationSettings.reminder_body}
                        onChange={(e) => setNotificationSettings({...notificationSettings, reminder_body: e.target.value})}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Redirect URL & Recurring */}
                <Card className="border-slate-100">
                  <CardHeader>
                    <CardTitle>Impostazioni Aggiuntive</CardTitle>
                    <CardDescription>URL redirect e prenotazioni ricorrenti</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4 max-w-xl">
                    <div className="space-y-2">
                      <Label>URL Redirect dopo prenotazione</Label>
                      <p className="text-xs text-slate-500">Il cliente verrà reindirizzato a questo URL dopo aver completato la prenotazione</p>
                      <Input
                        placeholder="myapp://home oppure https://tuosito.com/grazie"
                        value={notificationSettings.redirect_url_after_booking}
                        onChange={(e) => setNotificationSettings({...notificationSettings, redirect_url_after_booking: e.target.value})}
                      />
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
                        <p className="text-xs text-blue-800">
                          <strong>Per App Nativa (WebView):</strong> Usa un URL scheme personalizzato come <code className="bg-blue-100 px-1 rounded">myapp://home</code> o <code className="bg-blue-100 px-1 rounded">myapp://booking-complete</code>
                        </p>
                        <p className="text-xs text-blue-700 mt-1">
                          L'app iOS/Android deve essere configurata per gestire questo URL scheme.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 pt-4 border-t">
                      <Switch 
                        id="recurring-enabled" 
                        checked={notificationSettings.recurring_appointments_enabled}
                        onCheckedChange={(checked) => setNotificationSettings({...notificationSettings, recurring_appointments_enabled: checked})}
                      />
                      <div>
                        <Label htmlFor="recurring-enabled">Appuntamenti Ricorrenti</Label>
                        <p className="text-xs text-slate-500">Permetti ai clienti di prenotare appuntamenti ricorrenti</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Save Button */}
                <Button 
                  onClick={handleSaveNotificationSettings}
                  disabled={savingNotifications}
                  className="w-full max-w-xl"
                >
                  {savingNotifications ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Salva Impostazioni Notifiche
                </Button>
              </div>
            )}
            
            {/* INTEGRATION - Integrazioni */}
            {infoSubTab === "integration" && (
              <div className="space-y-6">
                {/* Link e Iframe */}
                <Card className="border-slate-100">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2"><Globe className="h-5 w-5" />Pagina Prenotazione</CardTitle>
                    <CardDescription>Link e codice per integrare la prenotazione sul tuo sito</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {business?.slug ? (
                      <>
                        <div>
                          <Label className="text-sm font-medium">Link Diretto</Label>
                          <div className="flex items-center gap-2 mt-2">
                            <Input readOnly value={`${window.location.origin}/book/${business.slug}`} className="font-mono text-sm" />
                            <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/book/${business.slug}`); toast.success("Link copiato!"); }}>Copia</Button>
                            <Button variant="outline" size="sm" onClick={() => window.open(`/book/${business.slug}`, '_blank')}><ExternalLink className="h-4 w-4" /></Button>
                          </div>
                        </div>
                        <div>
                          <Label className="text-sm font-medium">Codice Iframe</Label>
                          <div className="bg-slate-900 rounded-lg p-3 mt-2 overflow-x-auto">
                            <code className="text-green-400 text-xs">{`<iframe src="${window.location.origin}/book/${business.slug}" width="100%" height="800" frameborder="0"></iframe>`}</code>
                          </div>
                          <Button variant="outline" size="sm" className="mt-2" onClick={() => { navigator.clipboard.writeText(`<iframe src="${window.location.origin}/book/${business.slug}" width="100%" height="800" frameborder="0"></iframe>`); toast.success("Codice copiato!"); }}>Copia Codice</Button>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-4 text-slate-500">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                        <p>Caricamento dati attività...</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
                
                {/* Google Calendar Integration */}
                <Card className="border-slate-100">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2"><Calendar className="h-5 w-5" />Google Calendar</CardTitle>
                    <CardDescription>Sincronizza le prenotazioni con Google Calendar</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {!gcalStatus.configured ? (
                      <div className="text-center py-4 bg-amber-50 rounded-lg border border-amber-200">
                        <p className="text-amber-700">Google Calendar non configurato.</p>
                      </div>
                    ) : gcalStatus.connected ? (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center"><Calendar className="h-5 w-5 text-green-600" /></div>
                            <div><p className="font-medium text-green-800">Collegato</p><p className="text-sm text-green-600">{gcalStatus.email}</p></div>
                          </div>
                          <Button variant="outline" className="text-red-600 hover:bg-red-50" onClick={handleDisconnectGoogleCalendar}><Unlink className="h-4 w-4 mr-2" />Disconnetti</Button>
                        </div>
                        <div className="p-4 bg-slate-50 rounded-lg border">
                          <Label className="text-sm font-medium mb-2 block">Calendario</Label>
                          {loadingGoogleCalendars ? (
                            <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento...</div>
                          ) : (googleCalendars && googleCalendars.length > 0) ? (
                            <Select value={gcalStatus.selected_calendar_id || "primary"} onValueChange={(val) => { const cal = googleCalendars.find(c => c.id === val); handleSelectGoogleCalendar(val, cal?.summary || "Principale"); }}>
                              <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                              <SelectContent>
                                {googleCalendars.map(cal => (
                                  <SelectItem key={cal.id} value={cal.id}>
                                    <div className="flex items-center gap-2">
                                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cal.backgroundColor || '#4285f4' }}></div>
                                      {cal.summary} {cal.primary && "(Principale)"}
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <p className="text-sm text-slate-500">Nessun calendario disponibile</p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <Button onClick={handleConnectGoogleCalendar} disabled={connectingGcal}>
                        {connectingGcal ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Link className="h-4 w-4 mr-2" />}
                        Collega Google Calendar
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
            
            {/* PAYMENT - Pagamenti */}
            {infoSubTab === "payment" && (
              <Card className="border-slate-100">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><CreditCard className="h-5 w-5" />Impostazioni Pagamento</CardTitle>
                  <CardDescription>Configura come i clienti pagano le prenotazioni</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSaveSettings} className="space-y-4 max-w-md">
                    <div className="space-y-2">
                      <Label>Modalità Pagamento</Label>
                      <Select value={formData.payment_mode} onValueChange={(v) => setFormData({ ...formData, payment_mode: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="disabled">Disabilitato - Nessun pagamento richiesto</SelectItem>
                          <SelectItem value="full">Pagamento Completo - 100% al momento della prenotazione</SelectItem>
                          <SelectItem value="percentage">Acconto - Percentuale al momento della prenotazione</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {formData.payment_mode === "percentage" && (
                      <div className="space-y-2">
                        <Label>Percentuale Acconto</Label>
                        <div className="flex items-center gap-2">
                          <Input type="number" min="1" max="100" value={formData.payment_percentage} onChange={(e) => setFormData({ ...formData, payment_percentage: parseInt(e.target.value) || 30 })} className="w-24" />
                          <span>%</span>
                        </div>
                      </div>
                    )}
                    <Button type="submit" disabled={saving}><Save className="h-4 w-4 mr-2" />{saving ? "Salvataggio..." : "Salva"}</Button>
                  </form>
                </CardContent>
              </Card>
            )}
            
            {/* EMAIL - Template Email */}
            {infoSubTab === "email" && (
              <div className="space-y-6">
                {/* Additional Message for Confirmation Emails */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Mail className="h-5 w-5" />
                      Messaggio Aggiuntivo Email
                    </CardTitle>
                    <CardDescription>
                      Inserisci un messaggio personalizzato che verrà incluso nelle email di conferma prenotazione
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Messaggio aggiuntivo</Label>
                      <textarea
                        className="w-full min-h-[100px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        placeholder="Es: Ricorda di portare un documento d'identità. / Si prega di arrivare 10 minuti prima dell'appuntamento."
                        defaultValue={business?.email_additional_message || ""}
                        data-testid="admin-email-additional-message"
                        onChange={(e) => setBusiness(prev => ({ ...prev, email_additional_message: e.target.value }))}
                      />
                      <p className="text-xs text-slate-500">Questo messaggio apparirà nelle email di conferma inviate ai clienti</p>
                    </div>
                    <Button 
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem("token");
                          await axios.put(`${API}/admin/businesses/${businessId}`, { email_additional_message: business?.email_additional_message || "" }, { headers: { Authorization: `Bearer ${token}` } });
                          toast.success("Messaggio salvato");
                        } catch (error) {
                          toast.error("Errore nel salvataggio");
                        }
                      }}
                      data-testid="admin-save-email-message-btn"
                    >
                      Salva Messaggio
                    </Button>
                  </CardContent>
                </Card>
                
                <EmailTemplatesManager role="admin" businessId={businessId} />
              </div>
            )}

            {/* WhatsApp Settings */}
            {infoSubTab === "whatsapp" && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <MessageCircle className="h-5 w-5 text-green-600" />
                    Configurazione WhatsApp
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label>Numero WhatsApp</Label>
                    <Input
                      value={formData.whatsapp_number || ""}
                      onChange={(e) => setFormData({...formData, whatsapp_number: e.target.value})}
                      placeholder="+39 333 1234567"
                      data-testid="admin-whatsapp-number-input"
                    />
                    <p className="text-xs text-slate-500">Con prefisso internazionale (es. +39)</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Template Messaggio</Label>
                    <textarea
                      className="w-full rounded-md border border-slate-200 bg-transparent px-3 py-2 text-sm min-h-[100px] focus:outline-none focus:ring-2 focus:ring-slate-400"
                      value={formData.whatsapp_template || "Ciao {nome_cliente}, ti ricordiamo il tuo appuntamento per {servizio} il {data} alle {ora}. A presto! {nome_attivita}"}
                      onChange={(e) => setFormData({...formData, whatsapp_template: e.target.value})}
                      data-testid="admin-whatsapp-template-input"
                    />
                    <div className="text-xs text-slate-500 space-y-1">
                      <p className="font-medium">Variabili disponibili:</p>
                      <div className="flex flex-wrap gap-2">
                        {["{nome_cliente}", "{servizio}", "{data}", "{ora}", "{nome_attivita}"].map(v => (
                          <code key={v} className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">{v}</code>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm font-medium text-green-800 mb-1">Anteprima messaggio</p>
                    <p className="text-sm text-green-700">
                      {(formData.whatsapp_template || "Ciao {nome_cliente}, ti ricordiamo il tuo appuntamento per {servizio} il {data} alle {ora}. A presto! {nome_attivita}")
                        .replace(/{nome_cliente}/g, "Mario Rossi")
                        .replace(/{servizio}/g, "Taglio + Piega")
                        .replace(/{data}/g, "15 agosto 2026")
                        .replace(/{ora}/g, "10:30")
                        .replace(/{nome_attivita}/g, business?.name || "La tua attività")}
                    </p>
                  </div>
                  <Button onClick={handleSaveSettings} data-testid="admin-save-whatsapp-settings">
                    <Save className="h-4 w-4 mr-2" />Salva Impostazioni WhatsApp
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          <TabsContent value="categories" className="mt-6">
            <Card className="border-slate-100">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div><CardTitle>Categorie</CardTitle><CardDescription>Organizza i servizi in categorie</CardDescription></div>
                  <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
                    <DialogTrigger asChild>
                      <Button onClick={() => { setEditingCategory(null); setCategoryForm({ name: "", description: "", image_url: "", order: 0, color: "#4F46E5" }); }}><Plus className="h-4 w-4 mr-2" />Aggiungi Categoria</Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle>{editingCategory ? "Modifica Categoria" : "Nuova Categoria"}</DialogTitle></DialogHeader>
                      <form onSubmit={handleSaveCategory} className="space-y-4">
                        <div className="space-y-2"><Label>Nome Categoria</Label><Input value={categoryForm.name} onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })} required /></div>
                        <div className="space-y-2">
                          <Label>Colore</Label>
                          <div className="flex items-center gap-2">
                            <input type="color" value={categoryForm.color || "#4F46E5"} onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })} className="w-16 h-10 p-1 rounded border cursor-pointer" />
                            <Input value={categoryForm.color || "#4F46E5"} onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })} className="flex-1" />
                          </div>
                        </div>
                        <div className="space-y-2"><Label>Immagine</Label><ImageUpload value={categoryForm.image_url} onChange={(url) => setCategoryForm({ ...categoryForm, image_url: url })} /></div>
                        <div className="space-y-2"><Label>Descrizione</Label><Textarea value={categoryForm.description} onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })} rows={2} /></div>
                        <DialogFooter><Button type="submit">{editingCategory ? "Salva" : "Crea"}</Button></DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {categories.length === 0 ? (
                  <div className="text-center py-8 text-slate-500"><FolderOpen className="h-12 w-12 mx-auto mb-3 text-slate-300" /><p>Nessuna categoria. Crea la prima per organizzare i servizi.</p></div>
                ) : (
                  <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleCategoryDragEnd}>
                    <SortableContext items={[...categories].sort((a, b) => (a.order || 0) - (b.order || 0)).map(c => c.category_id)} strategy={rectSortingStrategy}>
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-categories-grid">
                        {[...categories].sort((a, b) => (a.order || 0) - (b.order || 0)).map((cat) => {
                          const catServicesCount = services.filter(s => s.category_id === cat.category_id).length;
                          return (
                            <AdminSortableCategoryCard key={cat.category_id} id={cat.category_id}>
                              {({ listeners, isDragging }) => (
                                <Card className={`border-slate-200 relative group transition-shadow ${isDragging ? 'shadow-xl ring-2 ring-blue-400' : ''}`} data-testid={`admin-category-card-${cat.category_id}`}>
                                  <CardContent className="p-4">
                                    <div className="absolute top-0 left-0 right-0 h-1 rounded-t-lg" style={{ backgroundColor: cat.color || '#6B7280' }} />
                                    {cat.image_url && <img src={cat.image_url} alt={cat.name} className="w-full h-24 object-cover rounded-lg mb-3 mt-2" />}
                                    <div className="flex items-start justify-between">
                                      <div className="flex items-center gap-3 flex-1 min-w-0">
                                        <div {...listeners} className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-slate-200 transition-colors" title="Tieni premuto e trascina" data-testid={`admin-category-drag-handle-${cat.category_id}`}>
                                          <GripVertical className="h-4 w-4 text-slate-400" />
                                        </div>
                                        <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color || '#6B7280' }} />
                                        <div className="min-w-0">
                                          <h4 className="font-semibold truncate">{cat.name}</h4>
                                          {cat.description && <p className="text-sm text-slate-500">{cat.description}</p>}
                                          <p className="text-xs text-slate-400 mt-1">{catServicesCount} servizi</p>
                                        </div>
                                      </div>
                                      <div className="flex gap-1 flex-shrink-0">
                                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditingCategory(cat); setCategoryForm({ name: cat.name, description: cat.description || "", image_url: cat.image_url || "", order: cat.order || 0, color: cat.color || "#4F46E5" }); setCategoryDialogOpen(true); }}><Pencil className="h-4 w-4" /></Button>
                                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => handleDeleteCategory(cat.category_id)}><Trash2 className="h-4 w-4" /></Button>
                                      </div>
                                    </div>
                                  </CardContent>
                                </Card>
                              )}
                            </AdminSortableCategoryCard>
                          );
                        })}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Services Tab */}
          <TabsContent value="services" className="mt-6">
            <Card className="border-slate-100">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Servizi</CardTitle>
                    <CardDescription>
                      Servizi offerti da questa attività 
                      <Badge variant="outline" className="ml-2 text-xs">
                        {business?.business_type === "event" ? "👥 Eventi" : business?.business_type === "daily_rental" ? "🏖️ Noleggio" : "🕐 Appuntamenti"}
                      </Badge>
                    </CardDescription>
                  </div>
                  <Dialog open={serviceDialogOpen} onOpenChange={setServiceDialogOpen}>
                    <DialogTrigger asChild>
                      <Button onClick={() => { setEditingService(null); setServiceForm({ name: "", description: "", image_url: "", category_id: "", duration_minutes: 30, price: 0, currency: "EUR", max_capacity: 1, min_seats: 1, max_seats_per_booking: 10, price_per_day: 0, min_days: 1, max_days: 30 }); }}><Plus className="h-4 w-4 mr-2" />Aggiungi Servizio</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                      <DialogHeader><DialogTitle>{editingService ? "Modifica Servizio" : "Nuovo Servizio"}</DialogTitle></DialogHeader>
                      <form onSubmit={handleSaveService} className="space-y-4">
                        {/* Service type is inherited from business - show info */}
                        <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-sm">
                          <span className="text-slate-600">Tipo servizio: </span>
                          <span className="font-medium">
                            {business?.business_type === "event" ? "👥 Evento/Attività" : business?.business_type === "daily_rental" ? "🏖️ Noleggio Giornaliero" : "🕐 Appuntamento"}
                          </span>
                          <p className="text-xs text-slate-400 mt-1">Il tipo è determinato dall&apos;attività</p>
                        </div>
                        <div className="space-y-2"><Label>Nome Servizio</Label><Input value={serviceForm.name} onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })} required /></div>
                        <div className="space-y-2"><Label>Categoria</Label>
                          <Select value={serviceForm.category_id || "none"} onValueChange={(v) => setServiceForm({ ...serviceForm, category_id: v === "none" ? "" : v })}>
                            <SelectTrigger><SelectValue placeholder="Seleziona categoria" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Nessuna categoria</SelectItem>
                              {categories.map((c) => (
                                <SelectItem key={c.category_id} value={c.category_id}>
                                  <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: c.color || '#6B7280' }}></div>
                                    {c.name}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2"><Label>Immagine</Label><ImageUpload value={serviceForm.image_url} onChange={(url) => setServiceForm({ ...serviceForm, image_url: url })} shape="square" /></div>
                        
                        {/* Fields based on business type */}
                        {business?.business_type === "appointment" && (
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2"><Label>Durata (min)</Label><Input type="number" min="5" value={serviceForm.duration_minutes} onChange={(e) => setServiceForm({ ...serviceForm, duration_minutes: parseInt(e.target.value) || 30 })} /></div>
                            <div className="space-y-2"><Label>Prezzo (€)</Label><Input type="number" min="0" step="0.01" value={serviceForm.price} onChange={(e) => setServiceForm({ ...serviceForm, price: parseFloat(e.target.value) || 0 })} /></div>
                          </div>
                        )}
                        
                        {business?.business_type === "event" && (
                          <>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2"><Label>Durata (min)</Label><Input type="number" min="5" value={serviceForm.duration_minutes} onChange={(e) => setServiceForm({ ...serviceForm, duration_minutes: parseInt(e.target.value) || 30 })} /></div>
                              <div className="space-y-2"><Label>Prezzo/Posto (€)</Label><Input type="number" min="0" step="0.01" value={serviceForm.price} onChange={(e) => setServiceForm({ ...serviceForm, price: parseFloat(e.target.value) || 0 })} /></div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                              <div className="space-y-2"><Label>Posti Totali</Label><Input type="number" min="1" value={serviceForm.max_capacity} onChange={(e) => setServiceForm({ ...serviceForm, max_capacity: parseInt(e.target.value) || 1 })} /></div>
                              <div className="space-y-2"><Label>Min Posti</Label><Input type="number" min="1" value={serviceForm.min_seats} onChange={(e) => setServiceForm({ ...serviceForm, min_seats: parseInt(e.target.value) || 1 })} /></div>
                              <div className="space-y-2"><Label>Max/Pren.</Label><Input type="number" min="1" value={serviceForm.max_seats_per_booking} onChange={(e) => setServiceForm({ ...serviceForm, max_seats_per_booking: parseInt(e.target.value) || 10 })} /></div>
                            </div>
                          </>
                        )}
                        
                        {business?.business_type === "daily_rental" && (
                          <>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2"><Label>Prezzo/Giorno (€)</Label><Input type="number" min="0" step="0.01" value={serviceForm.price_per_day || serviceForm.price} onChange={(e) => setServiceForm({ ...serviceForm, price_per_day: parseFloat(e.target.value) || 0, price: parseFloat(e.target.value) || 0 })} /></div>
                              <div className="space-y-2"><Label>Quantità Disp.</Label><Input type="number" min="1" value={serviceForm.max_capacity} onChange={(e) => setServiceForm({ ...serviceForm, max_capacity: parseInt(e.target.value) || 1 })} /></div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2"><Label>Min Giorni</Label><Input type="number" min="1" value={serviceForm.min_days} onChange={(e) => setServiceForm({ ...serviceForm, min_days: parseInt(e.target.value) || 1 })} /></div>
                              <div className="space-y-2"><Label>Max Giorni</Label><Input type="number" min="1" value={serviceForm.max_days} onChange={(e) => setServiceForm({ ...serviceForm, max_days: parseInt(e.target.value) || 30 })} /></div>
                            </div>
                          </>
                        )}
                        
                        <div className="space-y-2"><Label>Descrizione</Label><Textarea value={serviceForm.description} onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })} rows={2} /></div>
                        <DialogFooter><Button type="submit">{editingService ? "Salva" : "Crea"}</Button></DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {/* Category Filter Buttons */}
                {categories.length > 0 && (
                  <div className="mb-4 flex gap-2 flex-wrap items-center">
                    <span className="text-sm text-slate-500 mr-2">Filtra per categoria:</span>
                    <button
                      onClick={() => setServiceCategoryFilter("all")}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                        serviceCategoryFilter === "all" 
                          ? "bg-slate-800 text-white" 
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                    >
                      Tutti ({services.length})
                    </button>
                    {categories.sort((a, b) => (a.order || 0) - (b.order || 0)).map((cat) => {
                      const count = services.filter(s => s.category_id === cat.category_id).length;
                      return (
                        <button
                          key={cat.category_id}
                          onClick={() => setServiceCategoryFilter(serviceCategoryFilter === cat.category_id ? "all" : cat.category_id)}
                          style={{
                            backgroundColor: serviceCategoryFilter === cat.category_id ? cat.color : `${cat.color}20`,
                            color: serviceCategoryFilter === cat.category_id ? '#fff' : cat.color
                          }}
                          className="px-3 py-1 rounded-full text-xs font-medium transition-all hover:opacity-80"
                        >
                          {cat.name} ({count})
                        </button>
                      );
                    })}
                    <button
                      onClick={() => setServiceCategoryFilter("none")}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                        serviceCategoryFilter === "none" 
                          ? "bg-slate-500 text-white" 
                          : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                      }`}
                    >
                      Senza categoria ({services.filter(s => !s.category_id).length})
                    </button>
                  </div>
                )}
                
                {services.length === 0 ? (
                  <div className="text-center py-8 text-slate-500"><Package className="h-12 w-12 mx-auto mb-3 text-slate-300" /><p>Nessun servizio.</p></div>
                ) : (() => {
                  const filteredServices = services
                    .filter(service => {
                      if (serviceCategoryFilter === "all") return true;
                      if (serviceCategoryFilter === "none") return !service.category_id;
                      return service.category_id === serviceCategoryFilter;
                    })
                    .sort((a, b) => (a.order || 0) - (b.order || 0));
                  const serviceIds = filteredServices.map(s => s.service_id);

                  return filteredServices.length > 0 ? (
                    <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleServiceDragEnd}>
                      <SortableContext items={serviceIds} strategy={rectSortingStrategy}>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-services-grid">
                          {filteredServices.map((service) => {
                            const cat = categories.find(c => c.category_id === service.category_id);
                            return (
                              <AdminSortableServiceCard key={service.service_id} id={service.service_id}>
                                {({ listeners, isDragging }) => (
                                  <Card className={`overflow-hidden transition-shadow ${isDragging ? 'shadow-xl ring-2 ring-blue-400' : 'hover:shadow-md'} ${service.is_active === false ? 'opacity-60' : ''}`} data-testid={`admin-service-card-${service.service_id}`}>
                                    {/* Drag handle */}
                                    <div
                                      {...listeners}
                                      className="flex items-center justify-center gap-1 py-1.5 bg-slate-50 border-b cursor-grab active:cursor-grabbing hover:bg-slate-100 transition-colors"
                                      title="Tieni premuto e trascina per riordinare"
                                      data-testid={`admin-service-drag-handle-${service.service_id}`}
                                    >
                                      <GripVertical className="h-4 w-4 text-slate-400" />
                                      <span className="text-[10px] text-slate-400 uppercase tracking-wider font-medium select-none">Trascina</span>
                                    </div>
                                    {/* Service Image */}
                                    {service.image_url ? (
                                      <div className="relative w-full h-20 bg-slate-100">
                                        <img src={service.image_url} alt={service.name} className="w-full h-full object-cover" />
                                        {cat && <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-medium text-white" style={{ backgroundColor: cat.color || '#6B7280' }}>{cat.name}</div>}
                                        {service.is_active === false && <Badge variant="destructive" className="absolute top-2 right-2 text-xs">Disattivo</Badge>}
                                      </div>
                                    ) : (
                                      <div className="h-24 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center relative">
                                        <Package className="h-8 w-8 text-slate-300" />
                                        {cat && <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-medium text-white" style={{ backgroundColor: cat.color || '#6B7280' }}>{cat.name}</div>}
                                        {service.is_active === false && <Badge variant="destructive" className="absolute top-2 right-2 text-xs">Disattivo</Badge>}
                                      </div>
                                    )}
                                    <CardContent className="p-4">
                                      <div className="flex items-start justify-between">
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: cat?.color || "#6B7280" }} />
                                            <h4 className="font-semibold text-slate-900 truncate">{service.name}</h4>
                                          </div>
                                          {service.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{service.description}</p>}
                                          <div className="flex items-center gap-3 mt-3 text-sm">
                                            <span className="flex items-center gap-1 text-slate-500">
                                              <Clock className="h-3 w-3" />
                                              {business?.business_type === "daily_rental" 
                                                ? `${service.min_days}-${service.max_days}gg` 
                                                : `${service.duration_minutes} min`}
                                            </span>
                                            <span className="font-bold text-slate-900">
                                              {business?.business_type === "daily_rental" 
                                                ? `€${(service.price_per_day || service.price)?.toFixed(2)}/gg`
                                                : business?.business_type === "event"
                                                  ? `€${service.price?.toFixed(2)}/posto`
                                                  : `€${service.price?.toFixed(2)}`}
                                            </span>
                                          </div>
                                          {business?.business_type === "event" && (
                                            <p className="text-xs text-slate-400 mt-1">{service.max_capacity} posti totali</p>
                                          )}
                                        </div>
                                      </div>
                                      {/* Action buttons */}
                                      <div className="flex items-center justify-between mt-4 pt-3 border-t">
                                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleToggleServiceActive(service)} title={service.is_active === false ? "Attiva" : "Disattiva"}>
                                          {service.is_active === false ? <ToggleLeft className="h-4 w-4 text-slate-400" /> : <ToggleRight className="h-4 w-4 text-green-500" />}
                                        </Button>
                                        <div className="flex gap-1">
                                          <Button variant="outline" size="sm" className="h-7" onClick={() => { setEditingService(service); setServiceForm({ name: service.name, description: service.description || "", image_url: service.image_url || "", category_id: service.category_id || "", duration_minutes: service.duration_minutes, price: service.price, currency: service.currency || "EUR", max_capacity: service.max_capacity || 1, min_seats: service.min_seats || 1, max_seats_per_booking: service.max_seats_per_booking || 10, price_per_day: service.price_per_day || service.price, min_days: service.min_days || 1, max_days: service.max_days || 30 }); setServiceDialogOpen(true); }}>
                                            <Pencil className="h-3 w-3 mr-1" />Modifica
                                          </Button>
                                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={() => handleDeleteService(service.service_id)}>
                                            <Trash2 className="h-3 w-3" />
                                          </Button>
                                        </div>
                                      </div>
                                    </CardContent>
                                  </Card>
                                )}
                              </AdminSortableServiceCard>
                            );
                          })}
                        </div>
                      </SortableContext>
                    </DndContext>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <Package className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                      <p>Nessun servizio in questa categoria</p>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Staff Tab */}
          <TabsContent value="staff" className="mt-6">
            <Card className="border-slate-100">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div><CardTitle>Staff</CardTitle><CardDescription>Membri dello staff - ogni membro può avere il proprio calendario</CardDescription></div>
                  <Dialog open={staffDialogOpen} onOpenChange={setStaffDialogOpen}>
                    <DialogTrigger asChild>
                      <Button onClick={() => { setEditingStaff(null); setStaffForm({ name: "", email: "", phone: "", image_url: "", role: "employee", service_ids: [], is_active: true, color: "", has_login: false, password: "", can_edit_insert: true, can_delete: false, can_view_all_staff: false }); }}><Plus className="h-4 w-4 mr-2" />Aggiungi Staff</Button>
                    </DialogTrigger>
                    <DialogContent className="max-h-[90vh] overflow-y-auto">
                      <DialogHeader><DialogTitle>{editingStaff ? "Modifica Staff" : "Nuovo Staff"}</DialogTitle></DialogHeader>
                      <form onSubmit={handleSaveStaff} className="space-y-4">
                        <div className="space-y-2"><Label>Foto</Label><ImageUpload value={staffForm.image_url} onChange={(url) => setStaffForm({ ...staffForm, image_url: url })} shape="round" /></div>
                        <div className="space-y-2"><Label>Nome</Label><Input value={staffForm.name} onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })} required /></div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2"><Label>Email</Label><Input type="email" value={staffForm.email} onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })} /></div>
                          <div className="space-y-2"><Label>Telefono</Label><Input value={staffForm.phone} onChange={(e) => setStaffForm({ ...staffForm, phone: e.target.value })} /></div>
                        </div>
                        <div className="space-y-2"><Label>Ruolo</Label>
                          <Select value={staffForm.role} onValueChange={(v) => setStaffForm({ ...staffForm, role: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="manager">Manager</SelectItem><SelectItem value="employee">Dipendente</SelectItem></SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Colore Agenda</Label>
                          <div className="flex items-center gap-3">
                            <div className="flex gap-1.5 flex-wrap">
                              {["#3B82F6","#EF4444","#10B981","#F59E0B","#8B5CF6","#EC4899","#06B6D4","#F97316","#6366F1","#14B8A6"].map(c => (
                                <button
                                  key={c}
                                  type="button"
                                  className={`w-7 h-7 rounded-full border-2 transition-all ${staffForm.color === c ? 'border-slate-900 scale-110 ring-2 ring-offset-1 ring-slate-400' : 'border-transparent hover:scale-105'}`}
                                  style={{ backgroundColor: c }}
                                  onClick={() => setStaffForm({ ...staffForm, color: c })}
                                />
                              ))}
                            </div>
                            <input
                              type="color"
                              value={staffForm.color || "#3B82F6"}
                              onChange={(e) => setStaffForm({ ...staffForm, color: e.target.value })}
                              className="w-8 h-8 rounded cursor-pointer border-0 p-0"
                              title="Colore personalizzato"
                            />
                          </div>
                          {staffForm.color && (
                            <button type="button" className="text-xs text-slate-500 hover:text-red-500" onClick={() => setStaffForm({ ...staffForm, color: "" })}>
                              Rimuovi colore
                            </button>
                          )}
                        </div>
                        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <Label>Staff Attivo</Label>
                            <p className="text-xs text-slate-500">Se disattivato, non sarà visibile nelle prenotazioni</p>
                          </div>
                          <Switch checked={staffForm.is_active !== false} onCheckedChange={(checked) => setStaffForm({ ...staffForm, is_active: checked })} />
                        </div>
                        
                        {/* Staff Login Section */}
                        <div className="border rounded-lg p-4 space-y-3 bg-blue-50/50">
                          <div className="flex items-center justify-between">
                            <div>
                              <Label className="text-base">Accesso Portale Staff</Label>
                              <p className="text-xs text-slate-500">Permette allo staff di accedere al proprio portale</p>
                            </div>
                            <Switch 
                              checked={staffForm.has_login || false} 
                              onCheckedChange={(checked) => setStaffForm({ ...staffForm, has_login: checked, password: checked ? staffForm.password : "" })} 
                            />
                          </div>
                          
                          {staffForm.has_login && (
                            <div className="space-y-3 pt-2 border-t">
                              <div className="space-y-2">
                                <Label>Password {!editingStaff && '*'}</Label>
                                <Input 
                                  type="password" 
                                  value={staffForm.password || ""} 
                                  onChange={(e) => setStaffForm({ ...staffForm, password: e.target.value })}
                                  placeholder={editingStaff ? "Lascia vuoto per mantenere" : "Password per il portale"}
                                  required={!editingStaff}
                                />
                              </div>
                              
                              <div className="space-y-2">
                                <Label className="text-sm font-medium">Permessi</Label>
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between p-2 bg-white rounded border">
                                    <div>
                                      <span className="text-sm">Modifica/Inserisci Appuntamenti</span>
                                    </div>
                                    <Switch 
                                      checked={staffForm.can_edit_insert !== false} 
                                      onCheckedChange={(checked) => setStaffForm({ ...staffForm, can_edit_insert: checked })} 
                                    />
                                  </div>
                                  <div className="flex items-center justify-between p-2 bg-white rounded border">
                                    <div>
                                      <span className="text-sm">Elimina Appuntamenti</span>
                                    </div>
                                    <Switch 
                                      checked={staffForm.can_delete || false} 
                                      onCheckedChange={(checked) => setStaffForm({ ...staffForm, can_delete: checked })} 
                                    />
                                  </div>
                                  <div className="flex items-center justify-between p-2 bg-white rounded border">
                                    <div>
                                      <span className="text-sm">Vedi Tutti gli Staff</span>
                                    </div>
                                    <Switch 
                                      checked={staffForm.can_view_all_staff || false} 
                                      onCheckedChange={(checked) => setStaffForm({ ...staffForm, can_view_all_staff: checked })} 
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        {services.length > 0 && (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <Label>Servizi</Label>
                              <div className="flex gap-2">
                                <Button 
                                  type="button" 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => setStaffForm({ ...staffForm, service_ids: services.map(s => s.service_id) })}
                                >
                                  Seleziona tutti
                                </Button>
                                <Button 
                                  type="button" 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => setStaffForm({ ...staffForm, service_ids: [] })}
                                >
                                  Deseleziona tutti
                                </Button>
                              </div>
                            </div>
                            <div className="space-y-4 max-h-60 overflow-y-auto border rounded-md p-3">
                              {/* Group services by category */}
                              {categories.map((cat) => {
                                const catServices = services.filter(s => s.category_id === cat.category_id);
                                if (catServices.length === 0) return null;
                                const allCatSelected = catServices.every(s => staffForm.service_ids.includes(s.service_id));
                                const someCatSelected = catServices.some(s => staffForm.service_ids.includes(s.service_id));
                                return (
                                  <div key={cat.category_id}>
                                    <div className="flex items-center gap-2 mb-2 pb-1 border-b">
                                      <Checkbox 
                                        id={`admin-staff-cat-${cat.category_id}`}
                                        checked={allCatSelected}
                                        className={someCatSelected && !allCatSelected ? "opacity-50" : ""}
                                        onCheckedChange={(checked) => {
                                          if (checked) {
                                            const newIds = [...new Set([...staffForm.service_ids, ...catServices.map(s => s.service_id)])];
                                            setStaffForm({ ...staffForm, service_ids: newIds });
                                          } else {
                                            const catServiceIds = catServices.map(s => s.service_id);
                                            setStaffForm({ ...staffForm, service_ids: staffForm.service_ids.filter(id => !catServiceIds.includes(id)) });
                                          }
                                        }}
                                      />
                                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color || '#6B7280' }}></div>
                                      <label htmlFor={`admin-staff-cat-${cat.category_id}`} className="text-sm font-semibold">{cat.name}</label>
                                      <span className="text-xs text-slate-500">({catServices.filter(s => staffForm.service_ids.includes(s.service_id)).length}/{catServices.length})</span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-1 pl-5">
                                      {catServices.map((s) => (
                                        <div key={s.service_id} className="flex items-center gap-2">
                                          <Checkbox 
                                            id={`admin-staff-srv-${s.service_id}`} 
                                            checked={staffForm.service_ids.includes(s.service_id)} 
                                            onCheckedChange={(checked) => setStaffForm({ 
                                              ...staffForm, 
                                              service_ids: checked 
                                                ? [...staffForm.service_ids, s.service_id] 
                                                : staffForm.service_ids.filter(id => id !== s.service_id) 
                                            })} 
                                          />
                                          <label htmlFor={`admin-staff-srv-${s.service_id}`} className="text-sm truncate">{s.name}</label>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })}
                              {/* Services without category */}
                              {services.filter(s => !s.category_id).length > 0 && (
                                <div>
                                  <div className="flex items-center gap-2 mb-2 pb-1 border-b">
                                    <Checkbox 
                                      id="admin-staff-cat-none"
                                      checked={services.filter(s => !s.category_id).every(s => staffForm.service_ids.includes(s.service_id))}
                                      onCheckedChange={(checked) => {
                                        const uncatServices = services.filter(s => !s.category_id);
                                        if (checked) {
                                          const newIds = [...new Set([...staffForm.service_ids, ...uncatServices.map(s => s.service_id)])];
                                          setStaffForm({ ...staffForm, service_ids: newIds });
                                        } else {
                                          const uncatIds = uncatServices.map(s => s.service_id);
                                          setStaffForm({ ...staffForm, service_ids: staffForm.service_ids.filter(id => !uncatIds.includes(id)) });
                                        }
                                      }}
                                    />
                                    <div className="w-3 h-3 rounded-full bg-slate-400"></div>
                                    <label htmlFor="admin-staff-cat-none" className="text-sm font-semibold text-slate-600">Senza categoria</label>
                                  </div>
                                  <div className="grid grid-cols-2 gap-1 pl-5">
                                    {services.filter(s => !s.category_id).map((s) => (
                                      <div key={s.service_id} className="flex items-center gap-2">
                                        <Checkbox 
                                          id={`admin-staff-srv-${s.service_id}`} 
                                          checked={staffForm.service_ids.includes(s.service_id)} 
                                          onCheckedChange={(checked) => setStaffForm({ 
                                            ...staffForm, 
                                            service_ids: checked 
                                              ? [...staffForm.service_ids, s.service_id] 
                                              : staffForm.service_ids.filter(id => id !== s.service_id) 
                                          })} 
                                        />
                                        <label htmlFor={`admin-staff-srv-${s.service_id}`} className="text-sm truncate">{s.name}</label>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        <DialogFooter><Button type="submit">{editingStaff ? "Salva" : "Aggiungi"}</Button></DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {staff.length === 0 ? (
                  <div className="text-center py-8 text-slate-500"><Users className="h-12 w-12 mx-auto mb-3 text-slate-300" /><p>Nessun membro dello staff.</p></div>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {staff.map((member) => (
                      <Card key={member.staff_id} className={`border-slate-200 ${member.is_active === false ? 'opacity-60' : ''}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            {member.image_url ? (
                              <img src={member.image_url} alt={member.name} className="w-16 h-16 rounded-full object-cover" />
                            ) : (
                              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center"><User className="h-8 w-8 text-primary" /></div>
                            )}
                            <div className="flex-1">
                              <div className="flex items-start justify-between">
                                <div>
                                  <h4 className="font-semibold">{member.name}</h4>
                                  <div className="flex items-center gap-2 mt-1">
                                    <Badge variant="outline">{member.role === 'manager' ? 'Manager' : 'Dipendente'}</Badge>
                                    {member.is_active === false && <Badge variant="destructive" className="text-xs">Inattivo</Badge>}
                                    {member.has_login && <Badge variant="secondary" className="text-xs">Accesso Portale</Badge>}
                                  </div>
                                </div>
                                <div className="flex gap-1">
                                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleToggleStaffActive(member)}  title={member.is_active === false ? "Attiva" : "Disattiva"}>
                                    {member.is_active === false ? <ToggleLeft className="h-4 w-4 text-slate-400" /> : <ToggleRight className="h-4 w-4 text-green-500" />}
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditingStaff(member); setStaffForm({ name: member.name, email: member.email || "", phone: member.phone || "", image_url: member.image_url || "", role: member.role || "employee", service_ids: getStaffServiceIds(member), is_active: member.is_active !== false, color: member.color || "", has_login: member.has_login || false, password: "", can_edit_insert: member.can_edit_insert !== false, can_delete: member.can_delete || false, can_view_all_staff: member.can_view_all_staff || false }); setStaffDialogOpen(true); }}><Pencil className="h-4 w-4" /></Button>
                                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => handleDeleteStaff(member.staff_id)}><Trash2 className="h-4 w-4" /></Button>
                                </div>
                              </div>
                              {member.email && <p className="text-sm text-slate-500 mt-2">{member.email}</p>}
                              <div className="flex flex-wrap gap-1 mt-2">
                                {member.service_ids?.slice(0, 3).map(sid => {
                                  const svc = services.find(s => s.service_id === sid);
                                  return svc ? <Badge key={sid} variant="secondary" className="text-xs">{svc.name}</Badge> : null;
                                })}
                                {member.service_ids?.length > 3 && <Badge variant="secondary" className="text-xs">+{member.service_ids.length - 3}</Badge>}
                              </div>
                              <p className="text-xs text-slate-400 mt-2">{calendars.filter(c => c.staff_id === member.staff_id).length} calendari assegnati</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Calendars Tab */}
          <TabsContent value="calendars" className="mt-6">
            <Card className="border-slate-100">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div><CardTitle>Calendari</CardTitle><CardDescription>Ogni membro staff può avere il proprio calendario. Manager vede tutti.</CardDescription></div>
                  <Dialog open={calendarDialogOpen} onOpenChange={setCalendarDialogOpen}>
                    <DialogTrigger asChild>
                      <Button onClick={() => {
                        setEditingCalendar(null);
                        setCalendarForm({
                          name: "", description: "", service_ids: [], staff_id: "",
                          slot_duration_minutes: 30, buffer_before: 0, buffer_after: 0, max_days_advance: 30,
                          default_availability: [
                            { day_of_week: 0, periods: [{ start_time: "09:00", end_time: "13:00" }, { start_time: "14:00", end_time: "18:00" }] },
                            { day_of_week: 1, periods: [{ start_time: "09:00", end_time: "13:00" }, { start_time: "14:00", end_time: "18:00" }] },
                            { day_of_week: 2, periods: [{ start_time: "09:00", end_time: "13:00" }, { start_time: "14:00", end_time: "18:00" }] },
                            { day_of_week: 3, periods: [{ start_time: "09:00", end_time: "13:00" }, { start_time: "14:00", end_time: "18:00" }] },
                            { day_of_week: 4, periods: [{ start_time: "09:00", end_time: "13:00" }, { start_time: "14:00", end_time: "18:00" }] },
                          ]
                        });
                      }}><Plus className="h-4 w-4 mr-2" />Aggiungi Calendario</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader><DialogTitle>{editingCalendar ? "Modifica Calendario" : "Nuovo Calendario"}</DialogTitle></DialogHeader>
                      <form onSubmit={handleSaveCalendar} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2"><Label>Nome Calendario *</Label><Input value={calendarForm.name} onChange={(e) => setCalendarForm({ ...calendarForm, name: e.target.value })} required /></div>
                          <div className="space-y-2"><Label>Assegna a Staff</Label>
                            <Select value={calendarForm.staff_id || "none"} onValueChange={(v) => setCalendarForm({ ...calendarForm, staff_id: v === "none" ? "" : v })}>
                              <SelectTrigger><SelectValue placeholder="Tutti (Manager)" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Tutti (Manager)</SelectItem>
                                {staff.map((s) => <SelectItem key={s.staff_id} value={s.staff_id}>{s.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        {services.length > 0 && (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <Label>Servizi disponibili (opzionale)</Label>
                              <div className="flex gap-2">
                                <Button 
                                  type="button" 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => setCalendarForm({ ...calendarForm, service_ids: services.map(s => s.service_id) })}
                                >
                                  Seleziona tutti
                                </Button>
                                <Button 
                                  type="button" 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => setCalendarForm({ ...calendarForm, service_ids: [] })}
                                >
                                  Deseleziona tutti
                                </Button>
                              </div>
                            </div>
                            <div className="border rounded-md p-3 max-h-60 overflow-y-auto space-y-4">
                              {/* Group services by category */}
                              {categories.map((cat) => {
                                const catServices = services.filter(s => s.category_id === cat.category_id);
                                if (catServices.length === 0) return null;
                                const allCatSelected = catServices.every(s => calendarForm.service_ids.includes(s.service_id));
                                const someCatSelected = catServices.some(s => calendarForm.service_ids.includes(s.service_id));
                                return (
                                  <div key={cat.category_id}>
                                    <div className="flex items-center gap-2 mb-2 pb-1 border-b">
                                      <Checkbox 
                                        id={`admin-cal-cat-${cat.category_id}`}
                                        checked={allCatSelected}
                                        className={someCatSelected && !allCatSelected ? "opacity-50" : ""}
                                        onCheckedChange={(checked) => {
                                          if (checked) {
                                            const newIds = [...new Set([...calendarForm.service_ids, ...catServices.map(s => s.service_id)])];
                                            setCalendarForm({ ...calendarForm, service_ids: newIds });
                                          } else {
                                            const catServiceIds = catServices.map(s => s.service_id);
                                            setCalendarForm({ ...calendarForm, service_ids: calendarForm.service_ids.filter(id => !catServiceIds.includes(id)) });
                                          }
                                        }}
                                      />
                                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color || '#6B7280' }}></div>
                                      <label htmlFor={`admin-cal-cat-${cat.category_id}`} className="text-sm font-semibold">{cat.name}</label>
                                      <span className="text-xs text-slate-500">({catServices.filter(s => calendarForm.service_ids.includes(s.service_id)).length}/{catServices.length})</span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-1 pl-5">
                                      {catServices.map((s) => (
                                        <div key={s.service_id} className="flex items-center gap-2">
                                          <Checkbox 
                                            id={`admin-cal-srv-${s.service_id}`} 
                                            checked={calendarForm.service_ids.includes(s.service_id)} 
                                            onCheckedChange={(checked) => setCalendarForm({ 
                                              ...calendarForm, 
                                              service_ids: checked 
                                                ? [...calendarForm.service_ids, s.service_id] 
                                                : calendarForm.service_ids.filter(id => id !== s.service_id) 
                                            })} 
                                          />
                                          <label htmlFor={`admin-cal-srv-${s.service_id}`} className="text-sm truncate">{s.name}</label>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })}
                              {/* Services without category */}
                              {services.filter(s => !s.category_id).length > 0 && (
                                <div>
                                  <div className="flex items-center gap-2 mb-2 pb-1 border-b">
                                    <Checkbox 
                                      id="admin-cal-cat-none"
                                      checked={services.filter(s => !s.category_id).every(s => calendarForm.service_ids.includes(s.service_id))}
                                      onCheckedChange={(checked) => {
                                        const uncatServices = services.filter(s => !s.category_id);
                                        if (checked) {
                                          const newIds = [...new Set([...calendarForm.service_ids, ...uncatServices.map(s => s.service_id)])];
                                          setCalendarForm({ ...calendarForm, service_ids: newIds });
                                        } else {
                                          const uncatIds = uncatServices.map(s => s.service_id);
                                          setCalendarForm({ ...calendarForm, service_ids: calendarForm.service_ids.filter(id => !uncatIds.includes(id)) });
                                        }
                                      }}
                                    />
                                    <div className="w-3 h-3 rounded-full bg-slate-400"></div>
                                    <label htmlFor="admin-cal-cat-none" className="text-sm font-semibold text-slate-600">Senza categoria</label>
                                  </div>
                                  <div className="grid grid-cols-2 gap-1 pl-5">
                                    {services.filter(s => !s.category_id).map((s) => (
                                      <div key={s.service_id} className="flex items-center gap-2">
                                        <Checkbox 
                                          id={`admin-cal-srv-${s.service_id}`} 
                                          checked={calendarForm.service_ids.includes(s.service_id)} 
                                          onCheckedChange={(checked) => setCalendarForm({ 
                                            ...calendarForm, 
                                            service_ids: checked 
                                              ? [...calendarForm.service_ids, s.service_id] 
                                              : calendarForm.service_ids.filter(id => id !== s.service_id) 
                                          })} 
                                        />
                                        <label htmlFor={`admin-cal-srv-${s.service_id}`} className="text-sm truncate">{s.name}</label>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        <div className="grid grid-cols-4 gap-4">
                          <div className="space-y-2"><Label>Durata Slot</Label><Input type="number" min="5" value={calendarForm.slot_duration_minutes} onChange={(e) => setCalendarForm({ ...calendarForm, slot_duration_minutes: parseInt(e.target.value) || 30 })} /></div>
                          <div className="space-y-2"><Label>Buffer Prima</Label><Input type="number" min="0" value={calendarForm.buffer_before} onChange={(e) => setCalendarForm({ ...calendarForm, buffer_before: parseInt(e.target.value) || 0 })} /></div>
                          <div className="space-y-2"><Label>Buffer Dopo</Label><Input type="number" min="0" value={calendarForm.buffer_after} onChange={(e) => setCalendarForm({ ...calendarForm, buffer_after: parseInt(e.target.value) || 0 })} /></div>
                          <div className="space-y-2"><Label>Max Giorni</Label><Input type="number" min="1" value={calendarForm.max_days_advance} onChange={(e) => setCalendarForm({ ...calendarForm, max_days_advance: parseInt(e.target.value) || 30 })} /></div>
                        </div>
                        <div className="space-y-2">
                          <Label>Orari di Apertura</Label>
                          <div className="space-y-2 border rounded-md p-3">
                            {[0, 1, 2, 3, 4, 5, 6].map((dayNum) => {
                              const dayAvail = calendarForm.default_availability.find(d => d.day_of_week === dayNum);
                              const isActive = !!dayAvail;
                              return (
                                <div key={dayNum} className="flex items-center gap-3">
                                  <div className="w-16">
                                    <Checkbox id={`day-${dayNum}`} checked={isActive} onCheckedChange={(checked) => {
                                      if (checked) setCalendarForm({ ...calendarForm, default_availability: [...calendarForm.default_availability, { day_of_week: dayNum, periods: [{ start_time: "09:00", end_time: "18:00" }] }] });
                                      else setCalendarForm({ ...calendarForm, default_availability: calendarForm.default_availability.filter(d => d.day_of_week !== dayNum) });
                                    }} />
                                    <label htmlFor={`day-${dayNum}`} className="ml-2 text-sm font-medium">{dayNames[dayNum]}</label>
                                  </div>
                                  {isActive && (
                                    <div className="flex-1 flex flex-wrap gap-2">
                                      {dayAvail.periods.map((period, idx) => (
                                        <div key={idx} className="flex items-center gap-1 bg-slate-50 rounded px-2 py-1">
                                          <Input type="time" value={period.start_time} onChange={(e) => {
                                            const newAvail = [...calendarForm.default_availability];
                                            const dayIdx = newAvail.findIndex(d => d.day_of_week === dayNum);
                                            newAvail[dayIdx].periods[idx].start_time = e.target.value;
                                            setCalendarForm({ ...calendarForm, default_availability: newAvail });
                                          }} className="w-24 h-8 text-sm" />
                                          <span>-</span>
                                          <Input type="time" value={period.end_time} onChange={(e) => {
                                            const newAvail = [...calendarForm.default_availability];
                                            const dayIdx = newAvail.findIndex(d => d.day_of_week === dayNum);
                                            newAvail[dayIdx].periods[idx].end_time = e.target.value;
                                            setCalendarForm({ ...calendarForm, default_availability: newAvail });
                                          }} className="w-24 h-8 text-sm" />
                                          {dayAvail.periods.length > 1 && <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => {
                                            const newAvail = [...calendarForm.default_availability];
                                            const dayIdx = newAvail.findIndex(d => d.day_of_week === dayNum);
                                            newAvail[dayIdx].periods = newAvail[dayIdx].periods.filter((_, i) => i !== idx);
                                            setCalendarForm({ ...calendarForm, default_availability: newAvail });
                                          }}><XCircle className="h-4 w-4" /></Button>}
                                        </div>
                                      ))}
                                      <Button type="button" variant="outline" size="sm" onClick={() => {
                                        const newAvail = [...calendarForm.default_availability];
                                        const dayIdx = newAvail.findIndex(d => d.day_of_week === dayNum);
                                        newAvail[dayIdx].periods.push({ start_time: "14:00", end_time: "18:00" });
                                        setCalendarForm({ ...calendarForm, default_availability: newAvail });
                                      }}><Plus className="h-3 w-3 mr-1" />Fascia</Button>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        <DialogFooter><Button type="submit">{editingCalendar ? "Salva" : "Crea"}</Button></DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {calendars.length === 0 ? (
                  <div className="text-center py-8 text-slate-500"><Calendar className="h-12 w-12 mx-auto mb-3 text-slate-300" /><p>Nessun calendario.</p></div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {calendars.map((cal) => {
                      const assignedStaff = staff.find(s => s.staff_id === cal.staff_id);
                      const calendarLink = `${window.location.origin}/book/${business?.slug}?calendar=${cal.calendar_id}`;
                      return (
                        <Card key={cal.calendar_id} className="border-slate-200">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <h4 className="font-semibold">{cal.name}</h4>
                                <Badge variant="outline" className="mt-1">{assignedStaff ? assignedStaff.name : "Tutti (Manager)"}</Badge>
                              </div>
                              <div className="flex gap-1">
                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditCalendar(cal)}><Pencil className="h-4 w-4" /></Button>
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => handleDeleteCalendar(cal.calendar_id)}><Trash2 className="h-4 w-4" /></Button>
                              </div>
                            </div>
                            <div className="text-sm space-y-1 text-slate-600">
                              <p>Slot: {cal.slot_duration_minutes} min</p>
                              <p>Anticipo max: {cal.max_days_advance} giorni</p>
                              {cal.service_ids?.length > 0 && <p>Servizi: {cal.service_ids.length}</p>}
                            </div>
                            {/* Calendar Link */}
                            <div className="mt-3 pt-3 border-t border-slate-100">
                              <div className="flex items-center gap-2">
                                <Input 
                                  value={calendarLink} 
                                  readOnly 
                                  className="text-xs h-8 bg-slate-50"
                                />
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="h-8 px-2"
                                  onClick={() => copyCalendarLink(cal.calendar_id)}
                                >
                                  {copiedCalendarId === cal.calendar_id ? (
                                    <Check className="h-4 w-4 text-green-500" />
                                  ) : (
                                    <Copy className="h-4 w-4" />
                                  )}
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="h-8 px-2"
                                  onClick={() => window.open(calendarLink, '_blank')}
                                >
                                  <ExternalLink className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
            <BlockedDatesManager apiBasePath={`/admin/businesses/${businessId}`} />
            <StaffAbsencesManager businessId={businessId} staff={staff} />
          </TabsContent>

          {/* Bookings/Presenze Tab */}
          <TabsContent value="bookings" className="mt-4">
            <BookingsListView 
              bookings={bookings}
              onRefresh={fetchBusinessData}
              isAdmin={true}
              businessId={businessId}
              business={business}
            />
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="mt-6">
            <AnalyticsView businessId={businessId} isAdmin={true} />
          </TabsContent>

          <TabsContent value="discounts" className="mt-6">
            <DiscountCodesManager 
              apiBasePath={`/admin/businesses/${businessId}`}
              services={services}
              categories={categories}
            />
          </TabsContent>

          <TabsContent value="api-keys" className="mt-6">
            <ApiKeysManager apiBasePath={`/admin/businesses/${businessId}`} />
          </TabsContent>
        </Tabs>

        {/* Booking Detail Dialog */}
        <Dialog open={bookingDetailOpen} onOpenChange={setBookingDetailOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CalendarDays className="h-5 w-5" />
                Dettagli Prenotazione
              </DialogTitle>
            </DialogHeader>
            
            {selectedBooking && !editingBooking && (
              <div className="space-y-4">
                {/* Customer Info */}
                <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                  <h4 className="font-semibold flex items-center gap-2">
                    <UserCircle className="h-4 w-4" /> Cliente
                  </h4>
                  <p className="text-lg font-medium">{selectedBooking.customer_name}</p>
                  {selectedBooking.customer_email && (
                    <p className="text-sm flex items-center gap-2">
                      <Mail className="h-3 w-3 text-slate-400" /> {selectedBooking.customer_email}
                    </p>
                  )}
                  {selectedBooking.customer_phone && (
                    <p className="text-sm flex items-center gap-2">
                      <Phone className="h-3 w-3 text-slate-400" /> {selectedBooking.customer_phone}
                    </p>
                  )}
                </div>

                {/* Booking Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Data e Ora</p>
                    <p className="font-medium">{formatDateTime(selectedBooking.start_datetime)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Stato</p>
                    {getStatusBadge(selectedBooking.status)}
                  </div>
                </div>

                {/* Services */}
                <div>
                  <p className="text-sm text-slate-500 mb-2">Servizi</p>
                  <div className="space-y-1">
                    {(selectedBooking.services || selectedBooking.services_details || []).map((s, i) => (
                      <div key={i} className="flex justify-between text-sm p-2 bg-slate-50 rounded">
                        <span>{s.name}</span>
                        <span className="font-medium">€{(s.price || 0).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                  {(selectedBooking.total_price > 0 || selectedBooking.payment_amount > 0) && (
                    <div className="flex justify-between mt-2 pt-2 border-t font-semibold">
                      <span>Totale</span>
                      <span className="text-green-600">€{(selectedBooking.total_price || selectedBooking.payment_amount || 0).toFixed(2)}</span>
                    </div>
                  )}
                </div>

                {/* Notes */}
                {selectedBooking.notes && (
                  <div>
                    <p className="text-sm text-slate-500 mb-1 flex items-center gap-1">
                      <FileText className="h-3 w-3" /> Note
                    </p>
                    <p className="text-sm bg-yellow-50 p-2 rounded">{selectedBooking.notes}</p>
                  </div>
                )}

                <DialogFooter className="flex justify-between">
                  <Button variant="destructive" onClick={handleDeleteBooking}>
                    <Trash2 className="h-4 w-4 mr-2" /> Elimina
                  </Button>
                  <Button onClick={() => setEditingBooking(true)}>
                    <Pencil className="h-4 w-4 mr-2" /> Modifica
                  </Button>
                </DialogFooter>
              </div>
            )}

            {selectedBooking && editingBooking && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nome Cliente</Label>
                    <Input value={bookingEditForm.customer_name} onChange={(e) => setBookingEditForm({...bookingEditForm, customer_name: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Stato</Label>
                    <Select value={bookingEditForm.status} onValueChange={(v) => setBookingEditForm({...bookingEditForm, status: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="confirmed">Confermato</SelectItem>
                        <SelectItem value="pending">In attesa</SelectItem>
                        <SelectItem value="completed">Completato</SelectItem>
                        <SelectItem value="cancelled">Annullato</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input type="email" value={bookingEditForm.customer_email} onChange={(e) => setBookingEditForm({...bookingEditForm, customer_email: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Telefono</Label>
                    <Input value={bookingEditForm.customer_phone} onChange={(e) => setBookingEditForm({...bookingEditForm, customer_phone: e.target.value})} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Data</Label>
                    <Input type="date" value={bookingEditForm.start_datetime} onChange={(e) => setBookingEditForm({...bookingEditForm, start_datetime: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Ora</Label>
                    <Input type="time" value={bookingEditForm.start_time} onChange={(e) => setBookingEditForm({...bookingEditForm, start_time: e.target.value})} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Note</Label>
                  <Textarea value={bookingEditForm.notes} onChange={(e) => setBookingEditForm({...bookingEditForm, notes: e.target.value})} rows={2} />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setEditingBooking(false)}>Annulla</Button>
                  <Button onClick={handleUpdateBooking}>Salva Modifiche</Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminBusinessManage;
